import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, type CSSProperties } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Col, Nav, Row, Tab, Form, Dropdown } from 'react-bootstrap';
import Carousel from 'react-bootstrap/Carousel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faUpload,
  faChevronRight,
  faChevronDown,
  faLayerGroup,
  faFileAlt,
  faCloudUpload,
  faExchangeAlt,
  faCheckCircle,
  faPaperPlane,
  faPlus,
  faInfoCircle,
  faListCheck,
  faPlay,
  faEllipsisV,
  faShieldAlt,
  faUser,
  faRobot,
  faClipboard
} from '@fortawesome/free-solid-svg-icons';
import Button from '../../../components/base/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import { useMainLayoutContext } from '../../../providers/MainLayoutProvider';
import {
  useGetProjectResultsQuery,
  useGetProjectListsQuery,
  useGet360AnalysisQuery,
  useUpdateProjectMutation,
  useGetWhitelistRulesQuery,
  useGenerateAuditReportMutation
} from './data-access/project.api';
import BiasDetection, { BiasVersionHistoryStrip } from '../Bias/BiasDetection';
import Fairness, { FairnessVersionHistoryStrip } from '../Bias/Fairness';
import Explainability from '../Bias/Explainability';
import PromptInjection from '../Bias/PromptInjection';
import AISafetyDetection from '../AISafety/AISafetyDetection';
import type { AISafetyDetectionRef } from '../AISafety/AISafetyDetection';
import type { PromptInjectionRef } from '../Bias/PromptInjection';
import { Project } from '@app/shared/types/project.type';
import OwaspTab from './Owasp/OwaspTab';
import OntologyTab from './Ontology/OntologyTab';
import InternalComplianceTab from './InternalCompliance/InternalComplianceTab';
import ExternalComplianceTab from './ExternalCompliance/ExternalComplianceTab';
import Review360Tab from './Review360/Review360Tab';
import FinishSubmitTab from './FinishSubmit/FinishSubmitTab';
import AuditReportTab from './AuditReport/AuditReportTab';
import AudioTab from './Audio/AudioTab';
import AIAssuranceTab from './AIAssuranceTab';
import ChatConversationLifeSciences from '../../apps/ethico-lifesciences/ChatConversation-LifeSciences';
import { useLocation, useNavigate } from 'react-router-dom';
import InventoryTab from '../../../components/tabs/InventoryTab';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import api from '../../../app/api/axios-api';
import { useDispatch, useSelector } from 'react-redux';
import { setToast } from '../../../components/Toasts/data-access/toast-reducer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { ProjectCreateActions } from '../create-project/data-access/+state/project-create.actions';
import { clearProjectResults, setProjectResults, selectProjectResults } from './data-access/project.reducer';
import { store } from '../../../app/shared/store';
import Tabs from '@mui/material/Tabs';
import MuiTab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import { ProjectCreationSelectors } from '../create-project/data-access/+state/project-create.selectors';
import LinearProgress from '@mui/material/LinearProgress';
import { useRolePermissions } from '@app/shared/hooks/useRolePermissions';
import { runProjectAnalysis } from '../shared/projectAnalysis.utils';
import {
  canReviewerSubmitReview,
  hasProjectChangedSinceLastReview,
  normalizeReviewStatus,
  shouldReopenReviewAfterProjectUpdate
} from '../shared/reviewSubmission.utils';
import {
  buildReviewerStudioDisplayPayload,
  getReviewerStudioResults,
  hasProjectFeatureAnalysisData,
  normalizeWhitelistRulesRecord,
  resolveProjectResultsForId
} from '../shared/governanceResultsAccess';
import { ensureReviewerGovernanceResults, getMissingReviewerGovernanceKeys } from '../shared/reviewerGovernance.utils';
import { useSentryProjectScope } from '../../../hooks/useSentryProjectScope';

const normalizeString = (value: string) => (value ? value.toLowerCase().replace(/[^a-z0-9]/g, '') : '');

function parseReviewerStudioTabFromSearch(search: string): number | null {
  const params = new URLSearchParams(search);
  if (params.get('nav') !== 'whitelist') return null;
  const tab = Number(params.get('whitelistTab'));
  return Number.isFinite(tab) && tab >= 0 ? tab : null;
}

const PROJECT_SIDE_NAV_ITEMS = new Set([
  'summary',
  'uploads',
  'dimensions',
  'whitelist',
  'ai-assurance',
  'ethico',
  'audit-report',
  'audio'
]);

function parseProjectSideNavFromSearch(search: string): string | null {
  const nav = new URLSearchParams(search).get('nav');
  return nav && PROJECT_SIDE_NAV_ITEMS.has(nav) ? nav : null;
}

function readReviewerStudioFromLocation(): { activeNavItem: string; whitelistTabValue: number } {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const tab = parseReviewerStudioTabFromSearch(search);
  if (tab !== null) {
    return { activeNavItem: 'whitelist', whitelistTabValue: tab };
  }
  const nav = parseProjectSideNavFromSearch(search);
  if (nav) {
    return { activeNavItem: nav, whitelistTabValue: 0 };
  }
  return { activeNavItem: 'dimensions', whitelistTabValue: 0 };
}

function patchDetailsTabsSearch(
  currentSearch: string,
  patch: Record<string, string>,
  removeKeys: string[] = []
): string {
  const params = new URLSearchParams(currentSearch);
  Object.entries(patch).forEach(([key, value]) => params.set(key, value));
  removeKeys.forEach(key => params.delete(key));
  return params.toString();
}

/** Local project nav â€” active row highlight */
const PROJECT_NAV_ACTIVE_BG = 'rgb(0 120 255 / 12%)';

interface TagInputProps {
  tags: string[];
  onAddTag: (value: string) => void;
  onRemoveTag: (index: number) => void;
  placeholder: string;
  disabled?: boolean;
}

/* eslint-disable react/prop-types */
const TagInput: React.FC<TagInputProps> = ({ tags, onAddTag, onRemoveTag, placeholder, disabled = false }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        onAddTag(inputValue.trim());
        setInputValue('');
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    const value = e.target.value;
    if (value.includes(',')) {
      const newTags = value
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);
      newTags.forEach(tag => onAddTag(tag));
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  return (
    <div className="tag-input-container">
      <div className="d-flex flex-wrap gap-1 mb-2">
        {tags.map((tag, index) => (
          <span key={index} className="badge bg-light text-dark border d-flex align-items-center gap-1">
            {tag}
            <button
              type="button"
              className="btn-close btn-close-sm"
              style={{ fontSize: '0.6rem', width: '12px', height: '12px' }}
              onClick={() => {
                if (!disabled) {
                  onRemoveTag(index);
                }
              }}
              disabled={disabled}
              aria-label="Remove tag"
            />
          </span>
        ))}
      </div>
      <input
        type="text"
        className="form-control form-control-sm"
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        style={{ fontSize: '0.875rem' }}
      />
      <small className="text-muted">Press Enter or comma to add tags</small>
    </div>
  );
};
/* eslint-enable react/prop-types */

const ProjectDetailsWithTabs = () => {
  const { setContentClass } = useMainLayoutContext();
  const location = useLocation();
  const navigate = useNavigate();
  /** CSA+ uses `/csa+/details-tabs`; classic layout uses `/project/details-tabs`. */
  const detailsTabsBasePath = location.pathname.startsWith('/csa+') ? '/csa+/details-tabs' : '/project/details-tabs';
  const locationState = (location.state as {
    projectTitle?: string;
    projectDomain?: string;
  }) || { projectTitle: undefined, projectDomain: undefined };
  const dispatch = useDispatch();
  const projectWhitelistDraft = useSelector(ProjectCreationSelectors.whitelistDraft);
  const projectResultsFromRedux = useSelector(selectProjectResults);

  const userId = localStorage.getItem('userId') || '';
  const {
    role: normalizedUserRole,
    canManageWhitelist,
    canSelectWhitelistRules,
    canSaveWhitelist
  } = useRolePermissions();
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('');
  const [controls, setControls] = useState<any[]>([]);
  const biasDetectionRef = useRef<import('../Bias/BiasDetection').BiasDetectionRef | null>(null);
  const [biasVersionCounts, setBiasVersionCounts] = useState({ ml: 0, llm: 0 });
  const fairnessRef = useRef<import('../Bias/Fairness').FairnessDetectionRef | null>(null);
  const [fairnessVersionCounts, setFairnessVersionCounts] = useState({ ml: 0, llm: 0 });
  const explainabilityRef = useRef<import('../Bias/Explainability').ExplainabilityRef | null>(null);
  const [explainabilityNavInfo, setExplainabilityNavInfo] = useState({
    total: 0,
    runsWithMl: 0,
    runsWithLlm: 0
  });
  const aiSafetyRef = useRef<AISafetyDetectionRef | null>(null);
  const [aiSafetyNavInfo, setAiSafetyNavInfo] = useState({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
  const promptInjectionRef = useRef<PromptInjectionRef | null>(null);
  const [promptNavInfo, setPromptNavInfo] = useState({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
  const [selectedDimensionId, setSelectedDimensionId] = useState<number | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [openMenuItems, setOpenMenuItems] = useState<string[]>(['dimensions']);
  const initialReviewerStudio = readReviewerStudioFromLocation();
  const [activeNavItem, setActiveNavItem] = useState<string>(initialReviewerStudio.activeNavItem);
  const [allDimensions, setAllDimensions] = useState<any[]>([]);
  const [isChangeProjectModalOpen, setIsChangeProjectModalOpen] = useState(false);
  const [isEditProjectModalOpen, setIsEditProjectModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDomain, setEditDomain] = useState('');
  const [editOverview, setEditOverview] = useState('');
  const [editEnvironment, setEditEnvironment] = useState('DEV');
  const [isCompleteTriggered, setIsCompleteTriggered] = useState(false);
  const [activeApi, setActiveApi] = useState<string | null>(null);
  const [whitelistTabValue, setWhitelistTabValue] = useState(initialReviewerStudio.whitelistTabValue);
  const previousSelectedProjectIdRef = useRef<string | null>(null);
  const [sensitiveAttributes, setSensitiveAttributes] = useState<{
    [key: string]: { setA: string[]; setB: string[] };
  } | null>(null);
  const [isLoadingSensitiveAttributes, setIsLoadingSensitiveAttributes] = useState(false);
  const [selectedWhitelistRules, setSelectedWhitelistRules] = useState<string[]>([]);
  const [newWhitelistEntries, setNewWhitelistEntries] = useState<
    { key: string; regulatory_body: string[]; justification: string }[]
  >([]);
  const [isWhitelistSaving, setIsWhitelistSaving] = useState(false);
  const [isReviewerGovernanceLoading, setIsReviewerGovernanceLoading] = useState(false);
  const reviewerGovernanceInFlightRef = useRef(false);

  const [whitelistRulesData, setWhitelistRulesData] = useState<Record<
    string,
    {
      regulatory_body?: string[];
      justification?: string;
      setA?: string[];
      setB?: string[];
    }
  > | null>(null);

  const [projectDetail, setProjectDetail] = useState<any | null>(null);
  const [isProjectDetailLoading, setIsProjectDetailLoading] = useState(false);
  const [projectDetailError, setProjectDetailError] = useState<string | null>(null);

  const [isRunInProgress, setIsRunInProgress] = useState(false);
  const [runStatusLabel, setRunStatusLabel] = useState<string | null>(null);
  const [generatedAuditReport, setGeneratedAuditReport] = useState<any>(null);

  /** Viewport-fixed sidebar (sticky breaks when html/body use overflow-x: hidden). */
  const sidebarPanelAnchorRef = useRef<HTMLDivElement>(null);
  const [sidebarFixedLayout, setSidebarFixedLayout] = useState<{ left: number; width: number } | null>(null);

  const updateSidebarFixedLayout = useCallback(() => {
    const measure = () => {
      const el = sidebarPanelAnchorRef.current;
      if (!el || typeof window === 'undefined' || window.innerWidth < 992) {
        setSidebarFixedLayout(null);
        return;
      }
      const r = el.getBoundingClientRect();
      if (r.width < 8) {
        setSidebarFixedLayout(null);
        return;
      }
      setSidebarFixedLayout({ left: r.left, width: r.width });
    };
    requestAnimationFrame(() => requestAnimationFrame(measure));
  }, []);

  const handleEditClick = () => {
    // Set form values from selected project
    if (selectedProject) {
      setEditTitle(selectedProject.title || '');
      setEditDomain(selectedProject.domain || '');
      setEditOverview(selectedProject.overview || '');
      setEditEnvironment(localStorage.getItem('SELECTED_ENV') || 'DEV');
    }
    setIsEditProjectModalOpen(true);
  };

  const handleCloseEditProjectModal = () => {
    setIsEditProjectModalOpen(false);
  };

  const handleUploadClick = () => {
    // Open upload modal
    setIsUploadModalOpen(true);
  };

  const handleChangeProjectClick = () => {
    setIsChangeProjectModalOpen(true);
  };

  const handleCloseChangeProjectModal = () => {
    setIsChangeProjectModalOpen(false);
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
    localStorage.setItem('projectId', projectId);
    setIsChangeProjectModalOpen(false);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  const handleReviewClick = async () => {
    await handleWhitelistNavClick();
    setIsReviewModalOpen(false);
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
  };

  const formatDate = (value: any) => {
    if (!value) return 'N/A';
    const iso = typeof value === 'string' ? value : value?.$date;
    if (!iso) return 'N/A';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  const extractFileName = (value?: string) => {
    if (!value) return 'N/A';
    const segments = value.split('/');
    return segments[segments.length - 1] || value;
  };

  const handleSubmitForReview = async () => {
    if (!selectedProjectId || !userId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project or user information is missing. Please select a project.'
        })
      );
      return;
    }

    try {
      await updateProject({
        projectId: selectedProjectId,
        userId,
        review_status: 'READY_FOR_REVIEW'
      }).unwrap();

      dispatch(
        setToast({
          show: true,
          title: 'Submitted for Review',
          message: 'Project has been marked as ready for review.'
        })
      );

      setIsReviewModalOpen(false);

      const updatedProject = await fetchProjectData(selectedProjectId);
      if (updatedProject) {
        setProjectDetail(updatedProject);
        dispatch(
          ProjectCreateActions.setActiveProject({
            projectData: updatedProject
          })
        );
      }

      await refetchProjectList();
    } catch (error) {
      console.error('Failed to submit project for review:', error);
      dispatch(
        setToast({
          show: true,
          title: 'Submission Failed',
          message: 'Could not update project status. Please try again.'
        })
      );
    }
  };

  const handleResetReviewStatus = async () => {
    if (!selectedProjectId || !userId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project or user information is missing. Please select a project.'
        })
      );
      return;
    }

    try {
      await updateProject({
        projectId: selectedProjectId,
        userId,
        review_status: 'PENDING'
      }).unwrap();

      dispatch(
        setToast({
          show: true,
          title: 'Status Reset',
          message: 'Project review status has been reset to pending.'
        })
      );

      const updatedProject = await fetchProjectData(selectedProjectId);
      if (updatedProject) {
        setProjectDetail(updatedProject);
        dispatch(
          ProjectCreateActions.setActiveProject({
            projectData: updatedProject
          })
        );
      }

      await refetchProjectList();
    } catch (error) {
      console.error('Failed to reset project review status:', error);
      dispatch(
        setToast({
          show: true,
          title: 'Reset Failed',
          message: 'Could not reset project status. Please try again.'
        })
      );
    }
  };

  const fetchProjectData = useCallback(async (projectId: string) => {
    try {
      const response = await api.get(`/project/get-projects-by-id?projectId=${projectId}`);
      const data = response.data;
      const project = data?.project ?? data;
      if (!project || typeof project !== 'object') {
        return project;
      }
      const merged: Record<string, unknown> = { ...project };
      // When backend returns a full collection envelope, merge top-level fields onto the project for Redux/graphs
      if (data?.collection_results && typeof data.collection_results === 'object') {
        merged.collection_results = data.collection_results;
      }
      if (typeof data?.collection === 'string') {
        merged.collection = data.collection;
      }
      if (typeof data?.environment === 'string') {
        merged.environment = data.environment;
      }
      if (typeof data?.review_status === 'string') {
        merged.review_status = data.review_status;
      }
      if (typeof data?.projectId === 'string' && merged.projectId == null) {
        merged.projectId = data.projectId;
      }
      if (typeof data?.domain === 'string' && merged.domain == null) {
        merged.domain = data.domain;
      }
      if (data?.whitelist_rules != null) {
        merged.whitelist_rules = data.whitelist_rules;
      }
      return merged;
    } catch (error) {
      console.error('Error fetching project data:', error);
      throw error;
    }
  }, []);

  const handleCompleteClick = async () => {
    if (!selectedProjectId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'No project selected'
        })
      );
      return;
    }

    setIsCompleteTriggered(true);
    handleCloseUploadModal();

    try {
      // First fetch the complete project data
      setActiveApi('Fetching Project Data...');
      const projectData = await fetchProjectData(selectedProjectId);

      const resolvedProjectId = projectData?.projectId || selectedProject?.projectId || selectedProjectId;
      const resolvedUserId = projectData?.userId || userId;

      if (!resolvedProjectId || !resolvedUserId) {
        throw new Error('Missing project or user identifiers.');
      }

      await runProjectAnalysis({
        projectId: resolvedProjectId,
        userId: resolvedUserId,
        projectData,
        selectedProject,
        is_rerun: false,
        onStatusUpdate: (status: string) => {
          setActiveApi(status);
        },
        fetchProjectData
      });

      setIsCompleteTriggered(false);
      setActiveApi(null);

      dispatch(
        setToast({
          show: true,
          title: 'Process Completed',
          message: 'All analysis executed successfully'
        })
      );

      // Refetch project detail, list, and results so Bias, Fairness, etc. update without page refresh
      const refreshedProject = await fetchProjectData(resolvedProjectId);
      setProjectDetail(refreshedProject);
      await refetchProjectList?.();
      await reopenReviewIfProjectWasUpdated(refreshedProject as Record<string, unknown>);

      if (refetchProjectResults) await refetchProjectResults();
      if (refetch360Analysis) refetch360Analysis();

      // Delayed retry: backend may need a few seconds to persist results
      setTimeout(async () => {
        if (refetchProjectResults) await refetchProjectResults();
        if (refetch360Analysis) refetch360Analysis();
        const retryProject = await fetchProjectData(resolvedProjectId);
        setProjectDetail(retryProject);
        await reopenReviewIfProjectWasUpdated(retryProject as Record<string, unknown>);
      }, 3000);
    } catch (error: any) {
      console.error('Error during API execution:', error);
      setIsCompleteTriggered(false);
      setActiveApi(null);
      dispatch(
        setToast({
          show: true,
          title: 'Execution Failed',
          message: error?.response?.data?.message || error?.message || 'An error occurred while executing the process'
        })
      );
    }
  };

  // Use RTK Query instead of Redux Saga
  const environment = localStorage.getItem('SELECTED_ENV') || 'DEV';
  const { data: projectList = [], refetch: refetchProjectList } = useGetProjectListsQuery(
    { userId, environment },
    {
      skip: !userId // Skip if no userId
    }
  );

  const selectedProject = projectList.find((item: Project) => item.projectId === selectedProjectId);

  const activeProjectDomain = useMemo(() => {
    const domain = selectedProject?.domain || projectDetail?.domain || locationState.projectDomain;
    return domain && String(domain).trim() ? String(domain).trim() : 'N/A';
  }, [selectedProject?.domain, projectDetail?.domain, locationState.projectDomain]);

  const handleReviewSubmitted = useCallback(async () => {
    if (!selectedProjectId) return;
    const updatedProject = await fetchProjectData(selectedProjectId);
    if (updatedProject) {
      setProjectDetail(updatedProject);
      dispatch(
        ProjectCreateActions.setActiveProject({
          projectData: updatedProject
        })
      );
    }
    await refetchProjectList();
  }, [selectedProjectId, fetchProjectData, dispatch, refetchProjectList]);

  const projectForReviewWorkflow = useMemo(() => {
    const merged: Record<string, unknown> = {
      ...(selectedProject ? (selectedProject as Record<string, unknown>) : {}),
      ...(projectDetail ? (projectDetail as Record<string, unknown>) : {})
    };
    return Object.keys(merged).length > 0 ? merged : null;
  }, [selectedProject, projectDetail]);

  useSentryProjectScope({
    projectId: selectedProjectId,
    projectTitle: selectedProject?.title,
    activeTab,
    environment,
    route: 'details-tabs'
  });

  const workbenchHasBiasTab = useMemo(
    () => controls.some(c => normalizeString(c.control_title || '').includes('bias')),
    [controls]
  );
  const workbenchHasFairnessTab = useMemo(
    () => controls.some(c => normalizeString(c.control_title || '').includes('fair')),
    [controls]
  );
  const workbenchHasExplainTab = useMemo(
    () => controls.some(c => normalizeString(c.control_title || '').includes('explain')),
    [controls]
  );
  const workbenchHasAiSafetyTab = useMemo(
    () =>
      controls.some(c => {
        const n = normalizeString(c.control_title || '');
        return n.includes('safety') || n.includes('abuse') || (n.includes('responsible') && n.includes('ai'));
      }),
    [controls]
  );
  const workbenchHasPromptTab = useMemo(
    () =>
      controls.some(c => {
        const n = normalizeString(c.control_title || '');
        return n.includes('prompt') && n.includes('injection');
      }),
    [controls]
  );

  useEffect(() => {
    if (!workbenchHasBiasTab) setBiasVersionCounts({ ml: 0, llm: 0 });
    if (!workbenchHasFairnessTab) setFairnessVersionCounts({ ml: 0, llm: 0 });
    if (!workbenchHasExplainTab) setExplainabilityNavInfo({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
    if (!workbenchHasAiSafetyTab) setAiSafetyNavInfo({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
    if (!workbenchHasPromptTab) setPromptNavInfo({ total: 0, runsWithMl: 0, runsWithLlm: 0 });
  }, [
    workbenchHasBiasTab,
    workbenchHasFairnessTab,
    workbenchHasExplainTab,
    workbenchHasAiSafetyTab,
    workbenchHasPromptTab
  ]);

  useLayoutEffect(() => {
    updateSidebarFixedLayout();
    const el = sidebarPanelAnchorRef.current;
    const ro = new ResizeObserver(() => updateSidebarFixedLayout());
    if (el) {
      ro.observe(el);
    }
    const contentEl = document.querySelector('.content');
    if (contentEl) {
      ro.observe(contentEl);
    }
    window.addEventListener('resize', updateSidebarFixedLayout);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSidebarFixedLayout);
    };
  }, [updateSidebarFixedLayout, location.pathname]);

  // Fetch project details on selection change
  useEffect(() => {
    if (selectedProjectId) {
      setIsProjectDetailLoading(true);
      fetchProjectData(selectedProjectId)
        .then(data => {
          setProjectDetail(data);
          setIsProjectDetailLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch project details:', err);
          setProjectDetailError('Failed to fetch project details');
          setIsProjectDetailLoading(false);
        });
    }
  }, [selectedProjectId, fetchProjectData]);

  // Domain catalog + project-level rules from get-projects-by-id
  const whitelistRulesDomain = activeProjectDomain !== 'N/A' ? activeProjectDomain : undefined;
  const {
    data: globalWhitelistRules,
    isLoading: isGlobalRulesLoading,
    isFetching: isGlobalRulesFetching,
    refetch: refetchWhitelistRules
  } = useGetWhitelistRulesQuery(whitelistRulesDomain, {
    skip: !whitelistRulesDomain
  });

  // Merge domain catalog with project-level rules (re-run when project changes even if RTK cache is unchanged)
  useEffect(() => {
    const domainRules = normalizeWhitelistRulesRecord(globalWhitelistRules);
    const projectRules = normalizeWhitelistRulesRecord(projectDetail?.whitelist_rules);
    const mergedRules = { ...domainRules, ...projectRules } as Record<
      string,
      { regulatory_body?: string[]; justification?: string; setA?: string[]; setB?: string[] }
    >;
    setWhitelistRulesData(mergedRules);
  }, [globalWhitelistRules, projectDetail?.whitelist_rules, selectedProjectId, whitelistRulesDomain]);

  useEffect(() => {
    if (activeNavItem === 'whitelist' && whitelistTabValue === 1 && whitelistRulesDomain) {
      refetchWhitelistRules();
    }
  }, [activeNavItem, whitelistTabValue, whitelistRulesDomain, refetchWhitelistRules]);

  // Effect to sync selected rules from project details

  const [updateProject, { isLoading: isUpdatingProject }] = useUpdateProjectMutation();

  const reopenReviewIfProjectWasUpdated = useCallback(
    async (projectSnapshot: Record<string, unknown> | null | undefined) => {
      if (!selectedProjectId || !userId || !projectSnapshot) return;

      const status =
        (projectSnapshot.review_status as string | undefined) ?? (projectSnapshot.reviewStatus as string | undefined);
      if (!shouldReopenReviewAfterProjectUpdate(status)) return;

      try {
        await updateProject({
          projectId: selectedProjectId,
          userId,
          review_status: 'READY_FOR_REVIEW'
        }).unwrap();

        const updatedProject = await fetchProjectData(selectedProjectId);
        if (updatedProject) {
          setProjectDetail(updatedProject);
          dispatch(
            ProjectCreateActions.setActiveProject({
              projectData: updatedProject
            })
          );
        }
        await refetchProjectList();
      } catch (error) {
        console.error('Failed to reopen project for re-review:', error);
      }
    },
    [selectedProjectId, userId, updateProject, fetchProjectData, dispatch, refetchProjectList]
  );
  const [generateAuditReport, { isLoading: isGeneratingAuditReport }] = useGenerateAuditReportMutation();

  const handleSaveProject = async () => {
    if (!selectedProjectId || !userId) return;

    try {
      const requestBody: {
        projectId: string;
        userId: string;
        title?: string;
        domain?: string;
        overview?: string;
        environment?: string;
      } = {
        projectId: selectedProjectId,
        userId
      };

      // Only include optional fields if they have values
      if (editTitle) requestBody.title = editTitle;
      if (editDomain) requestBody.domain = editDomain;
      if (editOverview) requestBody.overview = editOverview;
      if (editEnvironment) requestBody.environment = editEnvironment;

      await updateProject(requestBody).unwrap();

      handleCloseEditProjectModal();

      // Optionally refetch project list to get updated data
      // You can dispatch a refresh action here if needed

      console.log('Project updated successfully');
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  // Load all dimensions from localStorage
  useEffect(() => {
    const featuresData = JSON.parse(localStorage.getItem('features') || '[]');
    setAllDimensions(featuresData);
  }, []);

  const toggleMenuItem = (menuItem: string) => {
    setOpenMenuItems(prev => (prev.includes(menuItem) ? prev.filter(item => item !== menuItem) : [...prev, menuItem]));
  };

  const handleNavItemClick = (item: string, dimensionId?: number) => {
    setActiveNavItem(item);
    if (item !== 'dimensions') return;

    if (dimensionId) {
      setSelectedDimensionId(dimensionId);
    }

    const searchPatch: Record<string, string> = {};
    if (dimensionId && selectedProjectId) {
      searchPatch['dimension-id'] = String(dimensionId);
      searchPatch.projectId = selectedProjectId;
    }

    navigate(
      `${detailsTabsBasePath}?${patchDetailsTabsSearch(location.search, searchPatch, ['nav', 'whitelistTab'])}`,
      { replace: true }
    );
  };

  const handleSummaryClick = () => {
    setActiveNavItem('summary');
    navigate(
      `${detailsTabsBasePath}?${patchDetailsTabsSearch(location.search, { nav: 'summary' }, ['whitelistTab'])}`,
      { replace: true }
    );
  };

  const handleUploadsNavClick = () => {
    setActiveNavItem('uploads');
    handleUploadClick(); // Open upload modal
  };

  const handleAIAssuranceNavClick = () => {
    setActiveNavItem('ai-assurance');
  };

  const handleEthicoNavClick = () => {
    setActiveNavItem('ethico');
  };

  const handleGenerateAuditReport = async () => {
    if (!selectedProjectId || !userId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project or user information is missing. Please select a project.'
        })
      );
      return;
    }

    try {
      const result = await generateAuditReport({
        projectId: selectedProjectId,
        userId: userId,
        reviewer_comment: ''
      }).unwrap();

      // Store the generated report
      setGeneratedAuditReport(result);

      dispatch(
        setToast({
          show: true,
          title: 'Success',
          message: 'Audit report generated successfully. Check the Audit Report tab.'
        })
      );

      // Navigate to Audit Report tab
      setActiveNavItem('audit-report');
    } catch (error: any) {
      console.error('Failed to generate audit report:', error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to generate audit report. Please try again.';

      // Clear the report on error
      setGeneratedAuditReport(null);

      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: errorMessage
        })
      );
    }
  };

  // Clear generated audit report when project changes
  useEffect(() => {
    setGeneratedAuditReport(null);
  }, [selectedProjectId]);

  const handleRunAnalysis = async () => {
    if (isRunInProgress) {
      return;
    }

    const projectId = selectedProject?.projectId || selectedProjectId || localStorage.getItem('projectId');
    const runUserId = selectedProject?.userId || userId;

    if (!projectId || !runUserId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Project or user information is missing. Please select a project.'
        })
      );
      return;
    }

    setIsRunInProgress(true);
    setRunStatusLabel('Fetching project data...');

    try {
      const projectData = await fetchProjectData(projectId);
      setProjectDetail(projectData);

      const meaid = uuidv4();

      await runProjectAnalysis({
        projectId,
        userId: runUserId,
        projectData,
        selectedProject,
        meaid,
        is_rerun: true,
        onStatusUpdate: (status: string) => {
          setRunStatusLabel(status);
        },
        fetchProjectData
      });

      setRunStatusLabel(null);
      dispatch(
        setToast({
          show: true,
          title: 'Process Completed',
          message: 'All analysis executed successfully.'
        })
      );

      // Refetch project detail, list, and results so Bias, Fairness, etc. update without page refresh
      const refreshedProject = await fetchProjectData(projectId);
      setProjectDetail(refreshedProject);
      await refetchProjectList?.();
      await reopenReviewIfProjectWasUpdated(refreshedProject as Record<string, unknown>);

      if (refetchProjectResults) await refetchProjectResults();
      if (refetch360Analysis) refetch360Analysis();

      // Delayed retry: backend may need a few seconds to persist results
      setTimeout(async () => {
        if (refetchProjectResults) await refetchProjectResults();
        if (refetch360Analysis) refetch360Analysis();
        const retryProject = await fetchProjectData(projectId);
        setProjectDetail(retryProject);
        await reopenReviewIfProjectWasUpdated(retryProject as Record<string, unknown>);
      }, 3000);
    } catch (error: any) {
      console.error('Error while running project analysis:', error);
      setRunStatusLabel(null);
      dispatch(
        setToast({
          show: true,
          title: 'Execution Failed',
          message: error?.response?.data?.message || error?.message || 'An error occurred while executing the process.'
        })
      );
    } finally {
      setIsRunInProgress(false);
    }
  };

  // Get dimension-id from URL and extract controls for that dimension
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dimId = parseInt(params.get('dimension-id') || '', 10);

    if (dimId) {
      setSelectedDimensionId(dimId);
    }
  }, [location.search]);

  // Keep Workbench / Reviewer Studio in sync with URL (survives refresh; avoids stale nav=whitelist)
  useEffect(() => {
    const tab = parseReviewerStudioTabFromSearch(location.search);
    if (tab !== null) {
      setActiveNavItem('whitelist');
      setWhitelistTabValue(tab);
      return;
    }

    const sideNav = parseProjectSideNavFromSearch(location.search);
    if (sideNav) {
      setActiveNavItem(sideNav);
      return;
    }

    const params = new URLSearchParams(location.search);
    if (!params.has('nav')) {
      setActiveNavItem(current => (current === 'whitelist' ? 'dimensions' : current));
    }
  }, [location.search]);

  // When projectId is in URL but no dimension-id, navigate to first dimension (keep other query params)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hasDimId = params.has('dimension-id');
    const projectId = params.get('projectId') || params.get('project-id');
    const featuresData = JSON.parse(localStorage.getItem('features') || '[]');
    const firstDimension = featuresData[0];

    if (!hasDimId && projectId && firstDimension?.dimension_id) {
      navigate(
        `${detailsTabsBasePath}?${patchDetailsTabsSearch(location.search, {
          'dimension-id': String(firstDimension.dimension_id),
          projectId: projectId.trim()
        })}`,
        { replace: true }
      );
    }
  }, [location.search, navigate, detailsTabsBasePath]);

  const projectIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const urlProjectId = params.get('projectId') || params.get('project-id') || '';
    return urlProjectId.trim();
  }, [location.search]);

  useEffect(() => {
    if (!projectIdFromUrl) return;
    if (selectedProjectId === projectIdFromUrl) return;
    setSelectedProjectId(projectIdFromUrl);
    localStorage.setItem('projectId', projectIdFromUrl);
  }, [projectIdFromUrl, selectedProjectId]);

  // Get features and extract controls for selected dimension
  useEffect(() => {
    if (!selectedDimensionId) return;

    const featuresData = JSON.parse(localStorage.getItem('features') || '[]');

    // Find the specific dimension
    const targetDimension = featuresData.find((dim: any) => dim.dimension_id === selectedDimensionId);

    if (targetDimension && targetDimension.controls) {
      // Filter out placeholder controls and map to dimension controls
      const filtered = targetDimension.controls.filter((control: any) => {
        const title = (control.control_title || '').toLowerCase();
        return !title.includes('placeholder');
      });

      const isEthicsDimension = normalizeString(targetDimension.dimension_title || '') === 'ethics';

      // For Ethics dimension, also remove AI Assurance from the list
      const filteredForDimension = isEthicsDimension
        ? filtered.filter((c: any) => normalizeString(c.control_title || '') !== 'aiassurance')
        : filtered;

      const dimensionControls = filteredForDimension.map((control: any) => ({
        ...control,
        dimension_id: targetDimension.dimension_id,
        dimension_title: targetDimension.dimension_title
      }));

      // Add AI Assurance tab only when not in Ethics dimension
      // if (!isEthicsDimension) {
      //   const hasAiAssurance = dimensionControls.some(
      //     (c: any) => normalizeString(c.control_title || '') === 'aiassurance'
      //   );
      //   if (!hasAiAssurance) {
      //     dimensionControls.push({
      //       control_title: 'AI Assurance',
      //       dimension_id: targetDimension.dimension_id,
      //       dimension_title: targetDimension.dimension_title
      //     });
      //   }
      // }

      setControls(dimensionControls);

      // Set default active tab to first control
      if (dimensionControls.length > 0) {
        setActiveTab(dimensionControls[0]?.control_title || '');
      } else {
        setActiveTab('');
      }
    } else {
      setControls([]);
      setActiveTab('');
    }
  }, [selectedDimensionId]);

  // Determine active project based on URL, stored value, or fallback
  useEffect(() => {
    if (projectList.length === 0) {
      return;
    }
    if (projectIdFromUrl) {
      return;
    }

    const storedProjectId = localStorage.getItem('projectId') || '';
    const candidateProjectId = projectIdFromUrl || storedProjectId || projectList[0].projectId;

    if (!candidateProjectId) {
      return;
    }

    if (candidateProjectId !== selectedProjectId) {
      setSelectedProjectId(candidateProjectId);
      localStorage.setItem('projectId', candidateProjectId);
    }
  }, [projectList, projectIdFromUrl, selectedProjectId]);

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setProjectDetail(null);
      return;
    }

    let isCancelled = false;
    setIsProjectDetailLoading(true);
    setProjectDetailError(null);

    fetchProjectData(selectedProjectId)
      .then(data => {
        if (!isCancelled) {
          setProjectDetail(data);
        }
      })
      .catch(error => {
        if (!isCancelled) {
          console.error('Failed to load project details:', error);
          setProjectDetail(null);
          setProjectDetailError('Failed to load project details.');
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsProjectDetailLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [selectedProjectId, fetchProjectData]);

  const projectUploads = projectDetail?.uploads ?? {};
  const datasetUploads = Array.isArray(projectUploads?.dataset) ? projectUploads.dataset : [];
  const mlModelUploads = Array.isArray(projectUploads?.ml_model) ? projectUploads.ml_model : [];
  const llmModelUploads = [
    ...(Array.isArray(projectUploads?.llm_models) ? projectUploads.llm_models : []),
    ...(Array.isArray((projectUploads as { llm_model?: unknown[] })?.llm_model)
      ? (projectUploads as { llm_model: unknown[] }).llm_model
      : [])
  ];
  const policyDocuments = Array.isArray(projectUploads?.policy_document) ? projectUploads.policy_document : [];
  const audioUploadsList = useMemo(() => {
    const audio = (projectUploads as any)?.audio;
    if (!Array.isArray(audio)) return [];
    return audio
      .map((a: any) => ({
        file_url: a.file_url || a.fileUrl,
        title: a.title,
        file_name: a.file_name || a.fileName
      }))
      .filter((x: { file_url: string }) => x.file_url);
  }, [projectUploads]);

  const isUploadsEmpty =
    datasetUploads.length === 0 &&
    mlModelUploads.length === 0 &&
    llmModelUploads.length === 0 &&
    policyDocuments.length === 0;

  // When project loads with empty uploads, navigate user to Uploads tab to see the empty state (once per project)
  const lastAutoSwitchedProjectId = useRef<string | null>(null);
  useEffect(() => {
    if (
      projectDetail &&
      !isProjectDetailLoading &&
      isUploadsEmpty &&
      selectedProjectId &&
      lastAutoSwitchedProjectId.current !== selectedProjectId
    ) {
      lastAutoSwitchedProjectId.current = selectedProjectId;
      setActiveNavItem('uploads');
    }
  }, [projectDetail, isProjectDetailLoading, isUploadsEmpty, selectedProjectId]);

  const llmBiasAggregate = (() => {
    const candidateSources = [
      projectDetail?.feature_results?.bias,
      projectDetail?.feature_results,
      projectDetail?.collection_results?.bias,
      projectDetail?.collection_results
    ].filter(source => source && typeof source === 'object');

    const llmKeys = [
      'LLMBiasDetection',
      'llmBiasDetection',
      'llm_bias_detection',
      'LLM_Bias_Detection',
      'LLM Bias Detection',
      'bias_llm',
      'biasLLM',
      'BiasLLM'
    ];

    for (const source of candidateSources as Record<string, any>[]) {
      for (const key of llmKeys) {
        const bucket = source[key];
        if (Array.isArray(bucket) && bucket.length > 0) {
          const latest = bucket[bucket.length - 1];
          if (latest?.aggregate_summary) {
            return latest.aggregate_summary;
          }
        } else if (bucket?.aggregate_summary) {
          return bucket.aggregate_summary;
        }
      }
      if (source?.aggregate_summary) {
        return source.aggregate_summary;
      }
    }

    return undefined;
  })();

  const activeProjectTitle = useMemo(() => {
    return selectedProject?.title || projectDetail?.title || locationState.projectTitle || 'Project';
  }, [selectedProject?.title, projectDetail?.title, locationState.projectTitle]);

  const whitelistEntries = Object.entries((projectDetail?.whitelist_rules as Record<string, any>) ?? {});

  const activeUsername = useMemo(() => {
    const firstName = localStorage.getItem('firstName');
    if (firstName) {
      return firstName;
    }
    // Fallback: extract first part of email if available
    const username = localStorage.getItem('username') || '';
    if (username) {
      const emailPart = username.split('@')[0];
      return emailPart.charAt(0).toUpperCase() + emailPart.slice(1);
    }
    return 'User';
  }, []);

  // Set active project in Redux for InventoryTab to access
  useEffect(() => {
    if (selectedProject) {
      dispatch(
        ProjectCreateActions.setActiveProject({
          projectData: selectedProject
        })
      );
    } else if (projectDetail) {
      dispatch(
        ProjectCreateActions.setActiveProject({
          projectData: projectDetail
        })
      );
    }
  }, [selectedProject, projectDetail, dispatch]);

  const shouldFetch = Boolean(selectedProjectId);
  const {
    data: projectResultsData,
    isLoading: isProjectResultsLoading,
    isError,
    refetch: refetchProjectResults
  } = useGetProjectResultsQuery(selectedProjectId!, {
    skip: !shouldFetch
  });

  const { refetch: refetch360Analysis } = useGet360AnalysisQuery(selectedProjectId!, {
    skip: !shouldFetch
  });

  // Sync project results to Redux — hydrate governance into `results` so Reviewer Studio tabs can render
  useEffect(() => {
    const existing = selectProjectResults(store.getState());

    if (projectResultsData && !projectResultsData?.error && hasProjectFeatureAnalysisData(projectResultsData)) {
      dispatch(
        setProjectResults(buildReviewerStudioDisplayPayload(projectResultsData as Record<string, unknown>, existing))
      );
      return;
    }
    const hasDetailResults =
      (projectDetail?.collection_results && typeof projectDetail.collection_results === 'object') ||
      (projectDetail?.results && typeof projectDetail.results === 'object') ||
      (projectDetail?.feature_results && typeof projectDetail.feature_results === 'object');
    if (projectDetail && hasDetailResults) {
      const detailRecord = projectDetail as Record<string, unknown>;
      const detailPayload: Record<string, unknown> = {
        collection_results: projectDetail.collection_results ?? {},
        results: projectDetail.results ?? {},
        feature_results: projectDetail.feature_results ?? {},
        projectId: projectDetail.projectId ?? selectedProjectId,
        project: projectDetail.project ?? projectDetail
      };
      if (detailRecord.fairness != null) detailPayload.fairness = detailRecord.fairness;
      if (detailRecord.fairness_metric != null) detailPayload.fairness_metric = detailRecord.fairness_metric;

      dispatch(setProjectResults(buildReviewerStudioDisplayPayload(detailPayload, existing)));
    }
  }, [projectResultsData, projectDetail, selectedProjectId, dispatch]);

  const reviewStatusRaw = (selectedProject as any)?.review_status ?? (selectedProject as any)?.reviewStatus ?? '';
  const normalizedReviewStatus = typeof reviewStatusRaw === 'string' ? reviewStatusRaw.trim().toLowerCase() : '';
  const projectChangedSinceReview = hasProjectChangedSinceLastReview(projectForReviewWorkflow);

  const canShowReviewerButton =
    (normalizedUserRole === 'reviewer' || normalizedUserRole === 'admin') &&
    (normalizedReviewStatus === 'ready_for_review' ||
      canReviewerSubmitReview(reviewStatusRaw, projectForReviewWorkflow));

  const canShowSubmitForReviewButton =
    normalizedUserRole === 'developer' &&
    (normalizedReviewStatus === 'pending' ||
      (normalizeReviewStatus(reviewStatusRaw) === 'APPROVED' && projectChangedSinceReview));

  const canShowDeveloperResetButton =
    normalizedUserRole === 'developer' && normalizedReviewStatus === 'ready_for_review';

  const showHeaderLinearProgress = isRunInProgress || isGeneratingAuditReport;
  const headerProgressLabel =
    runStatusLabel || (isGeneratingAuditReport ? 'Generating audit report...' : isRunInProgress ? activeApi : null);
  const reviewerGovernanceLoaderLabel = runStatusLabel || 'Loading governance results...';

  const handleWhitelistTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setWhitelistTabValue(newValue);
    const searchPatch: Record<string, string> = {
      nav: 'whitelist',
      whitelistTab: String(newValue)
    };
    if (selectedProjectId) {
      searchPatch.projectId = selectedProjectId;
    }
    navigate(`${detailsTabsBasePath}?${patchDetailsTabsSearch(location.search, searchPatch)}`, {
      replace: true
    });
  };

  const fetchSensitiveAttributes = useCallback(async () => {
    const domain = activeProjectDomain && activeProjectDomain !== 'N/A' ? activeProjectDomain : '';
    if (!domain) {
      setSensitiveAttributes(null);
      return;
    }

    setIsLoadingSensitiveAttributes(true);
    try {
      const response = await api.get(`/project/utils/sensitive_attributes?domain=${encodeURIComponent(domain)}`);

      if (response.data && response.data.sensitive_attributes) {
        setSensitiveAttributes(response.data.sensitive_attributes);
      } else {
        setSensitiveAttributes(null);
      }
    } catch (error) {
      console.error('Error fetching sensitive attributes:', error);
      setSensitiveAttributes(null);
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Failed to fetch sensitive attributes'
        })
      );
    } finally {
      setIsLoadingSensitiveAttributes(false);
    }
  }, [dispatch, activeProjectDomain]);

  useEffect(() => {
    if (activeNavItem === 'whitelist' && whitelistTabValue === 0) {
      fetchSensitiveAttributes();
    }
  }, [activeNavItem, whitelistTabValue, fetchSensitiveAttributes]);

  const fillMissingGovernanceIfNeeded = useCallback(
    async (projectResultsSnapshot: Record<string, unknown> | null) => {
      if (!selectedProjectId || !userId || reviewerGovernanceInFlightRef.current) return;

      const snapshot =
        projectResultsSnapshot ??
        resolveProjectResultsForId(selectedProjectId, projectResultsFromRedux, projectResultsData, projectDetail);
      if (!snapshot || !hasProjectFeatureAnalysisData(snapshot)) return;

      const displayPayload = buildReviewerStudioDisplayPayload(snapshot, projectResultsFromRedux);
      const resultsBucket = getReviewerStudioResults(displayPayload);
      const missing = getMissingReviewerGovernanceKeys(resultsBucket);
      if (missing.length === 0) return;

      const projectTitle =
        selectedProject?.title ||
        (projectDetail as { title?: string } | null)?.title ||
        (projectResultsFromRedux as { project?: { title?: string } } | null)?.project?.title ||
        'Project';

      reviewerGovernanceInFlightRef.current = true;
      setIsReviewerGovernanceLoading(true);
      setRunStatusLabel('Checking governance results...');

      try {
        const { called, failed, updatedCombinedPayload } = await ensureReviewerGovernanceResults({
          projectId: selectedProjectId,
          userId,
          title: projectTitle,
          combinedLlmPayload: snapshot,
          combinedLlmResults: resultsBucket,
          onStatusUpdate: setRunStatusLabel
        });

        dispatch(setProjectResults(buildReviewerStudioDisplayPayload(updatedCombinedPayload, projectResultsFromRedux)));

        if (called.length > 0) {
          setRunStatusLabel('Syncing..');
          if (refetchProjectResults) {
            const freshProjectResults = await refetchProjectResults();
            dispatch(
              setProjectResults(
                buildReviewerStudioDisplayPayload(
                  (freshProjectResults.data ?? {}) as Record<string, unknown>,
                  updatedCombinedPayload
                )
              )
            );
          }
          const refreshedProject = await fetchProjectData(selectedProjectId);
          if (refreshedProject) {
            setProjectDetail(refreshedProject);
          }
        }

        if (failed.length > 0) {
          dispatch(
            setToast({
              show: true,
              title: 'Reviewer Studio',
              message: `Some governance checks could not be generated (${failed.join(
                ', '
              )}). Other tabs may still be available.`
            })
          );
        }
      } catch (error) {
        console.error('Failed to ensure reviewer governance results:', error);
        dispatch(
          setToast({
            show: true,
            title: 'Reviewer Studio',
            message: 'Could not load governance analysis (OWASP, Ontology, Compliance). Please try again.'
          })
        );
      } finally {
        reviewerGovernanceInFlightRef.current = false;
        setIsReviewerGovernanceLoading(false);
        setRunStatusLabel(null);
      }
    },
    [
      selectedProjectId,
      userId,
      projectResultsFromRedux,
      projectResultsData,
      projectDetail,
      selectedProject?.title,
      refetchProjectResults,
      fetchProjectData,
      dispatch
    ]
  );

  useEffect(() => {
    if (activeNavItem !== 'whitelist' || !selectedProjectId) return;
    if (![3, 4, 5, 6].includes(whitelistTabValue)) return;
    const snapshot = resolveProjectResultsForId(
      selectedProjectId,
      projectResultsFromRedux,
      projectResultsData,
      projectDetail
    );
    const displayPayload = snapshot
      ? buildReviewerStudioDisplayPayload(snapshot, projectResultsFromRedux)
      : projectResultsFromRedux;
    if (getMissingReviewerGovernanceKeys(getReviewerStudioResults(displayPayload)).length === 0) {
      return;
    }
    fillMissingGovernanceIfNeeded(snapshot);
  }, [
    activeNavItem,
    whitelistTabValue,
    selectedProjectId,
    projectResultsFromRedux,
    projectResultsData,
    projectDetail,
    fillMissingGovernanceIfNeeded
  ]);

  const handleWhitelistNavClick = useCallback(async () => {
    const wasAlreadyOnReviewerStudio = activeNavItem === 'whitelist';
    const nextTab = wasAlreadyOnReviewerStudio ? whitelistTabValue : 0;
    setActiveNavItem('whitelist');
    setWhitelistTabValue(nextTab);

    const searchPatch: Record<string, string> = {
      nav: 'whitelist',
      whitelistTab: String(nextTab)
    };
    if (selectedProjectId) {
      searchPatch.projectId = selectedProjectId;
    }
    navigate(`${detailsTabsBasePath}?${patchDetailsTabsSearch(location.search, searchPatch)}`, {
      replace: true
    });

    const projectId = selectedProjectId;
    if (!projectId || !userId || reviewerGovernanceInFlightRef.current) {
      return;
    }

    if (wasAlreadyOnReviewerStudio) {
      return;
    }

    const projectResultsSnapshot = resolveProjectResultsForId(
      projectId,
      projectResultsFromRedux,
      projectResultsData,
      projectDetail
    );
    const reviewerStudioPayload = projectResultsSnapshot
      ? buildReviewerStudioDisplayPayload(projectResultsSnapshot, projectResultsFromRedux)
      : projectResultsFromRedux;

    if (reviewerStudioPayload) {
      dispatch(setProjectResults(reviewerStudioPayload));
    }

    if (!hasProjectFeatureAnalysisData(projectResultsSnapshot ?? reviewerStudioPayload)) {
      return;
    }

    await fillMissingGovernanceIfNeeded(projectResultsSnapshot);
  }, [
    activeNavItem,
    whitelistTabValue,
    selectedProjectId,
    userId,
    projectResultsFromRedux,
    projectResultsData,
    projectDetail,
    fillMissingGovernanceIfNeeded,
    dispatch,
    navigate,
    detailsTabsBasePath,
    location.search
  ]);

  useEffect(() => {
    // Helper to check if arrays are equal (unordered)
    const areArraysEqual = (arr1: string[], arr2: string[]) => {
      if (arr1.length !== arr2.length) return false;
      const set1 = new Set(arr1);
      return arr2.every(item => set1.has(item));
    };

    // Priority:
    // 1. Draft State (Redux) - if matching project (Preserve unsaved user changes)
    // 2. Project Detail (Authoritative Server Data) - if loaded
    // 3. Project List Item (Fallback)

    // Check availability
    const hasDraft = projectWhitelistDraft.projectId && projectWhitelistDraft.projectId === selectedProjectId;

    const hasProjectDetail = !!projectDetail?.whitelist_rules;

    let targetRules: string[] = [];
    let targetNewEntries: any[] = [];

    if (
      hasDraft &&
      (projectWhitelistDraft.selectedExistingRules.length > 0 || projectWhitelistDraft.newWhitelistRules.length > 0)
    ) {
      // 1. Draft State (Only if it has data, preventing empty init from overriding server data)
      targetRules = projectWhitelistDraft.selectedExistingRules;
      targetNewEntries = projectWhitelistDraft.newWhitelistRules;
    } else if (hasProjectDetail) {
      // 2. Authoritative Server Data (only if no draft exists)
      const sourceRules = projectDetail?.whitelist_rules;

      if (sourceRules) {
        if (Array.isArray(sourceRules)) {
          sourceRules.forEach((ruleObj: any) => {
            if (typeof ruleObj === 'string') {
              targetRules.push(ruleObj);
            } else if (typeof ruleObj === 'object') {
              Object.keys(ruleObj).forEach(key => {
                targetRules.push(key);
              });
            }
          });
        } else if (typeof sourceRules === 'object') {
          Object.keys(sourceRules).forEach(key => {
            targetRules.push(key);
          });
        }
      }
    } else {
      // 3. Fallback
      targetRules = Object.keys(((selectedProject as any)?.whitelist_rules as Record<string, any>) || {});
    }

    // Only update state if values are actually different to prevent infinite loops
    const rulesChanged = !areArraysEqual(selectedWhitelistRules, targetRules);

    // For new entries, we can use JSON.stringify for simple comparison
    const entriesChanged = JSON.stringify(newWhitelistEntries) !== JSON.stringify(targetNewEntries);

    if (rulesChanged) {
      // Intentionally omitting console log to reduce noise, unless debugging needed
      setSelectedWhitelistRules(targetRules);
    }

    if (entriesChanged) {
      setNewWhitelistEntries(targetNewEntries);
    }
  }, [projectDetail, projectWhitelistDraft, selectedProjectId]); // Ensure dependencies track changes

  // Reset transient UI state only when switching projects (not on mount / same id re-bind)
  useEffect(() => {
    const previousId = previousSelectedProjectIdRef.current;
    previousSelectedProjectIdRef.current = selectedProjectId;

    if (!selectedProjectId || previousId == null || previousId === selectedProjectId) return;

    dispatch(clearProjectResults());
    setSensitiveAttributes(null);
    setSelectedWhitelistRules([]);
    setNewWhitelistEntries([]);

    const urlTab = parseReviewerStudioTabFromSearch(location.search);
    if (urlTab !== null) {
      setActiveNavItem('whitelist');
      setWhitelistTabValue(urlTab);
    } else {
      setWhitelistTabValue(0);
    }
  }, [selectedProjectId, dispatch, location.search]);

  const handleWhitelistRuleChange = (ruleKey: string) => {
    if (!canSelectWhitelistRules) {
      return;
    }
    setSelectedWhitelistRules(prev =>
      prev.includes(ruleKey) ? prev.filter(rule => rule !== ruleKey) : [...prev, ruleKey]
    );
  };

  const handleAddNewWhitelistEntry = () => {
    if (!canManageWhitelist) {
      return;
    }
    setNewWhitelistEntries(prev => [...prev, { key: '', regulatory_body: [], justification: '' }]);
  };

  const handleNewEntryKeyChange = (index: number, value: string) => {
    if (!canManageWhitelist) {
      return;
    }
    setNewWhitelistEntries(prev => prev.map((entry, idx) => (idx === index ? { ...entry, key: value } : entry)));
  };

  const handleNewEntryJustificationChange = (index: number, value: string) => {
    if (!canManageWhitelist) {
      return;
    }
    setNewWhitelistEntries(prev =>
      prev.map((entry, idx) => (idx === index ? { ...entry, justification: value } : entry))
    );
  };

  const handleAddRegulatoryTag = (index: number, value: string) => {
    if (!canManageWhitelist) {
      return;
    }
    if (value.trim()) {
      setNewWhitelistEntries(prev =>
        prev.map((entry, idx) =>
          idx === index
            ? {
                ...entry,
                regulatory_body: [...entry.regulatory_body, value.trim()]
              }
            : entry
        )
      );
    }
  };

  const handleRemoveRegulatoryTag = (index: number, tagIndex: number) => {
    if (!canManageWhitelist) {
      return;
    }
    setNewWhitelistEntries(prev =>
      prev.map((entry, idx) =>
        idx === index
          ? {
              ...entry,
              regulatory_body: entry.regulatory_body.filter((_, tIdx) => tIdx !== tagIndex)
            }
          : entry
      )
    );
  };

  const handleRemoveNewEntry = (index: number) => {
    if (!canManageWhitelist) {
      return;
    }
    setNewWhitelistEntries(prev => prev.filter((_, idx) => idx !== index));
  };

  const prepareWhitelistSubmission = () => {
    const whitelistRules = whitelistRulesData || (selectedProject as any)?.whitelist_rules;
    const existingRulesPayload = selectedWhitelistRules.reduce(
      (acc, ruleKey) => {
        let ruleData: any = null;

        if (Array.isArray(whitelistRules)) {
          const matchingEntry = whitelistRules.find((r: any) => Object.keys(r || {}).includes(ruleKey));
          if (matchingEntry && matchingEntry[ruleKey]) {
            ruleData = matchingEntry[ruleKey];
          }
        } else if (whitelistRules && typeof whitelistRules === 'object' && ruleKey in whitelistRules) {
          ruleData = whitelistRules[ruleKey];
        }

        if (ruleData) {
          acc[ruleKey] = ruleData;
        }
        return acc;
      },
      {} as {
        [key: string]: {
          setA?: string[];
          setB?: string[];
          regulatory_body?: string[];
          justification?: string;
        };
      }
    );

    const newWhitelistRulesPayload = newWhitelistEntries.reduce(
      (acc, entry) => {
        if (entry.key.trim()) {
          acc[entry.key.trim()] = {
            regulatory_body: entry.regulatory_body,
            justification: entry.justification
          };
        }
        return acc;
      },
      {} as {
        [key: string]: { regulatory_body: string[]; justification: string };
      }
    );

    return {
      projectId: selectedProject?.projectId || selectedProjectId || null,
      userId,
      existingRulesPayload,
      selectedExistingRuleKeys: selectedWhitelistRules,
      newWhitelistRules: newWhitelistEntries,
      newWhitelistRulesPayload
    };
  };

  const handleWhitelistReset = () => {
    if (!canManageWhitelist) {
      return;
    }
    setSelectedWhitelistRules([]);
    setNewWhitelistEntries([]);
  };

  const handleWhitelistSave = async () => {
    if (!canSaveWhitelist) {
      dispatch(
        setToast({
          show: true,
          title: 'Read-Only Access',
          message: 'You do not have permission to modify whitelist rules.'
        })
      );
      return;
    }
    const targetProjectId = selectedProject?.projectId || selectedProjectId;

    if (!targetProjectId) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'No project selected for whitelist update.'
        })
      );
      return;
    }

    const submissionData = prepareWhitelistSubmission();
    const whitelistRules: Record<string, { regulatory_body: string[]; justification: string }> = {};

    submissionData.selectedExistingRuleKeys.forEach(ruleKey => {
      const ruleData = submissionData.existingRulesPayload[ruleKey];
      if (ruleData) {
        whitelistRules[ruleKey] = {
          regulatory_body: ruleData.regulatory_body || [],
          justification: ruleData.justification || ''
        };
      }
    });

    Object.entries(submissionData.newWhitelistRulesPayload).forEach(([ruleKey, ruleValue]) => {
      if (ruleKey.trim()) {
        whitelistRules[ruleKey.trim()] = ruleValue;
      }
    });

    if (Object.keys(whitelistRules).length === 0) {
      dispatch(
        setToast({
          show: true,
          title: 'No Rules Selected',
          message: 'Select or add at least one whitelist rule before saving.'
        })
      );
      return;
    }

    const payload = {
      projectId: targetProjectId,
      userId: submissionData.userId,
      whitelist_rules: whitelistRules
    };

    setIsWhitelistSaving(true);
    try {
      await api.post('/project/whitelist-rules', payload);

      // Refresh project data to reflect new whitelist configuration
      if (targetProjectId) {
        const updatedProject = await fetchProjectData(targetProjectId);
        if (updatedProject) {
          dispatch(
            ProjectCreateActions.setActiveProject({
              projectData: updatedProject
            })
          );
        }
      }
      await refetchProjectList();

      dispatch(
        ProjectCreateActions.updateWhitelistDraft({
          projectId: targetProjectId,
          selectedExistingRules: submissionData.selectedExistingRuleKeys,
          existingRulesPayload: submissionData.existingRulesPayload,
          newWhitelistRules: []
        })
      );

      dispatch(
        setToast({
          show: true,
          title: 'Whitelist Updated',
          message: 'Whitelist configuration saved successfully.'
        })
      );
    } catch (error) {
      console.error('Failed to save whitelist rules:', error);
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Failed to save whitelist configuration. Please try again.'
        })
      );
    } finally {
      setIsWhitelistSaving(false);
    }
  };

  useEffect(() => {
    const submissionData = prepareWhitelistSubmission();
    dispatch(
      ProjectCreateActions.updateWhitelistDraft({
        projectId: submissionData.projectId,
        selectedExistingRules: submissionData.selectedExistingRuleKeys,
        existingRulesPayload: submissionData.existingRulesPayload,
        newWhitelistRules: submissionData.newWhitelistRules
      })
    );
  }, [dispatch, selectedProject?.projectId, selectedWhitelistRules, newWhitelistEntries]);

  const renderExistingWhitelistRules = () => {
    if (isGlobalRulesLoading || isGlobalRulesFetching) {
      return (
        <div className="d-flex justify-content-center align-items-center py-4">
          <CircularProgress size={24} />
          <span className="ms-3 text-muted">Loading whitelist rules...</span>
        </div>
      );
    }

    // Use fetched global rules as the source of truth for "Available Rules"
    // If not fetched yet (or failed), fallback to project rules or empty
    const whitelistRules = whitelistRulesData || {};
    const hasObjectRules =
      whitelistRules &&
      !Array.isArray(whitelistRules) &&
      typeof whitelistRules === 'object' &&
      Object.keys(whitelistRules).length > 0;
    const hasArrayRules = Array.isArray(whitelistRules) && whitelistRules.length > 0;

    if (!hasObjectRules && !hasArrayRules) {
      const emptyMessage =
        activeProjectDomain === 'N/A'
          ? 'Set a project domain in project details to load whitelist rules for this domain.'
          : `No whitelist rules available for ${activeProjectDomain}.`;
      return (
        <div className="text-center py-4">
          <p className="text-muted mb-3 fs-8">{emptyMessage}</p>
        </div>
      );
    }

    if (hasObjectRules) {
      return Object.entries(whitelistRules as Record<string, any>).map(([category, rules]) => (
        <div key={category} className="mb-2 p-2 border rounded">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <FormControlLabel
              control={
                <Checkbox
                  // Check if this category is in our selected list (which we initialized from project details)
                  checked={selectedWhitelistRules.includes(category)}
                  onChange={() => handleWhitelistRuleChange(category)}
                  name={category}
                  size="small"
                  disabled={!canSelectWhitelistRules}
                  sx={{
                    color: '#6c757d',
                    '&.Mui-checked': {
                      color: '#0d6efd'
                    }
                  }}
                />
              }
              label={
                <span className="fw-semibold text-body-highlight" style={{ textTransform: 'capitalize' }}>
                  {category.replace(/_/g, ' ')}
                </span>
              }
              disabled={!canSelectWhitelistRules}
              sx={{ mb: 0 }}
            />

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <small className="text-primary fw-bold">Regulatory Body:</small>
              <div className="d-flex flex-wrap gap-1">
                {rules?.regulatory_body?.map((item: string, idx: number) => (
                  <span key={idx} className="badge bg-primary-subtle text-primary px-2 py-1 fs-9">
                    {item}
                  </span>
                ))}
              </div>
              {rules?.justification && (
                <Tooltip title={rules.justification} arrow placement="top">
                  <span style={{ cursor: 'pointer', color: '#0d6efd' }} aria-label="Justification tooltip">
                    <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      ));
    }

    if (hasArrayRules) {
      return (whitelistRules as any[]).map((ruleSet, index) => (
        <div key={index} className="mb-3">
          {Object.entries(ruleSet).map(([category, rules]: [string, any]) => (
            <div key={category} className="mb-3 p-3 border rounded">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedWhitelistRules.includes(category)}
                    onChange={() => handleWhitelistRuleChange(category)}
                    name={category}
                    size="small"
                    disabled={!canSelectWhitelistRules}
                    sx={{
                      color: '#6c757d',
                      '&.Mui-checked': {
                        color: '#0d6efd'
                      }
                    }}
                  />
                }
                label={
                  <span className="fw-semibold text-body-highlight" style={{ textTransform: 'capitalize' }}>
                    {category.replace(/_/g, ' ')}
                  </span>
                }
                disabled={!canSelectWhitelistRules}
                sx={{ mb: 1, width: '100%' }}
              />

              <div className="d-flex gap-3 ms-4 flex-wrap">
                <div className="flex-1">
                  <small className="text-primary fw-bold d-block mb-2">Set A:</small>
                  <div className="d-flex flex-wrap gap-1">
                    {rules.setA?.map((item: string, idx: number) => (
                      <span key={idx} className="badge bg-primary-subtle text-primary px-2 py-1 fs-9">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex-1">
                  <small className="text-info fw-bold d-block mb-2">Set B:</small>
                  <div className="d-flex flex-wrap gap-1">
                    {rules.setB?.map((item: string, idx: number) => (
                      <span key={idx} className="badge bg-info-subtle text-info px-2 py-1 fs-9">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {rules.justification && (
                <div className="mt-3">
                  <small className="text-muted d-block fw-semibold">Justification:</small>
                  <p className="text-muted fs-8 mb-0">{rules.justification}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ));
    }

    return null;
  };

  const renderSensitiveAttributesSection = () => {
    const domainLabel = activeProjectDomain !== 'N/A' ? activeProjectDomain : 'this project';

    if (isLoadingSensitiveAttributes) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <CircularProgress size={24} />
          <span className="ms-3 text-muted">Loading sensitive attributes...</span>
        </div>
      );
    }

    if (!sensitiveAttributes || Object.keys(sensitiveAttributes).length === 0) {
      const emptyMessage =
        activeProjectDomain === 'N/A'
          ? 'Set a project domain in project details to load sensitive attributes.'
          : `No sensitive attributes available for ${domainLabel}.`;
      return (
        <div className="text-center py-4">
          <p className="text-muted fs-8 mb-0">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <>
        <p className="text-muted mb-3 fs-8">Sensitive attributes available for this domain:</p>

        <div className="overflow-auto mb-3" style={{ maxHeight: '320px' }}>
          {Object.keys(sensitiveAttributes).map(attributeKey => (
            <div key={attributeKey} className="mb-3 p-3 border rounded">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="fw-semibold text-body-highlight" style={{ textTransform: 'capitalize' }}>
                  {attributeKey.replace(/_/g, ' ')}
                </span>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-primary fw-bold">Set A:</small>
                  <div className="d-flex flex-wrap gap-1">
                    {sensitiveAttributes[attributeKey].setA?.map((item: string, idx: number) => (
                      <span key={idx} className="badge bg-primary-subtle text-primary px-2 py-1 fs-9">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-info fw-bold">Set B:</small>
                  <div className="d-flex flex-wrap gap-1">
                    {sensitiveAttributes[attributeKey].setB?.map((item: string, idx: number) => (
                      <span key={idx} className="badge bg-info-subtle text-info px-2 py-1 fs-9">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-center">
          <small className="text-muted">Available sensitive attributes for {domainLabel}</small>
        </div>
      </>
    );
  };

  const renderWhitelistRulesSection = () => (
    <div>
      <h5 className="mb-3 text-body-highlight">Whitelist Rules Configuration</h5>
      <p className="text-muted mb-3 fs-8">Select existing whitelist rules or add new ones for your project:</p>
      {!canManageWhitelist && (
        <Box
          sx={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: 1,
            p: 2,
            mb: 3
          }}
        >
          <Typography variant="body2" color="text.secondary">
            You have read-only access to whitelist settings. Contact an administrator if you need to request changes.
          </Typography>
        </Box>
      )}

      <div className="overflow-auto mb-3" style={{ maxHeight: '400px' }}>
        {renderExistingWhitelistRules()}

        {newWhitelistEntries.map((entry, index) => (
          <div key={index} className="mb-3 p-3 border-2 border-dashed rounded bg-light">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-primary mb-0">New Whitelist Rule #{index + 1}</h6>
              <IconButton
                onClick={() => handleRemoveNewEntry(index)}
                size="small"
                sx={{ color: '#dc3545' }}
                disabled={!canManageWhitelist}
                aria-label="Remove new whitelist rule"
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </div>

            <div className="mb-3">
              <TextField
                fullWidth
                label="Rule Key"
                value={entry.key}
                onChange={e => handleNewEntryKeyChange(index, e.target.value)}
                placeholder="e.g., age, gender, location"
                size="small"
                disabled={!canManageWhitelist}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label text-primary fw-bold">Regulatory Body:</label>
              <TagInput
                tags={entry.regulatory_body}
                onAddTag={value => handleAddRegulatoryTag(index, value)}
                onRemoveTag={tagIndex => handleRemoveRegulatoryTag(index, tagIndex)}
                placeholder="e.g., HIPAA, GDPR, FDA GMLP"
                disabled={!canManageWhitelist}
              />
            </div>

            <div className="mb-3">
              <label className="form-label text-info fw-bold">Justification:</label>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={entry.justification}
                onChange={e => handleNewEntryJustificationChange(index, e.target.value)}
                placeholder="Provide justification for this whitelist rule..."
                size="small"
                disabled={!canManageWhitelist}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.875rem'
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem'
                  }
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex justify-content-between align-items-center pt-3 border-top flex-wrap gap-3">
        <div>
          <small className="text-muted d-block">Selected existing rules: {selectedWhitelistRules.length}</small>
          <small className="text-muted d-block">
            New rules: {newWhitelistEntries.filter(entry => entry.key.trim()).length}
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="phoenix-secondary"
            onClick={handleAddNewWhitelistEntry}
            className="px-3"
            disabled={!canManageWhitelist || isWhitelistSaving}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Rule
          </Button>
          {canSaveWhitelist && (
            <Button
              variant="phoenix-primary"
              onClick={handleWhitelistSave}
              className="px-3"
              disabled={isWhitelistSaving}
            >
              {isWhitelistSaving ? (
                <span className="d-flex align-items-center gap-2">
                  <CircularProgress size={16} /> Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </Button>
          )}
          <Button
            variant="phoenix-secondary"
            onClick={handleWhitelistReset}
            className="px-3"
            disabled={!canManageWhitelist}
          >
            Reset
          </Button>
        </div>
      </div>
    </div>
  );

  const renderWhitelistContent = () => (
    <div style={{ padding: '24px 32px' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Review
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={whitelistTabValue}
          onChange={handleWhitelistTabChange}
          aria-label="whitelist configuration tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              fontWeight: 600,
              textTransform: 'none',
              minHeight: '40px',
              minWidth: { xs: 'auto', sm: 160 },
              color: '#6c757d'
            },
            '& .Mui-selected': {
              color: '#0d6efd !important'
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#0d6efd'
            }
          }}
        >
          <MuiTab label="Sensitive Attributes" />
          <MuiTab label="Whitelist Rules" />
          <MuiTab label="Compliance IQ" />
          <MuiTab label="OWASP" />
          <MuiTab label="Ontology" />
          <MuiTab label="Internal Compliance" />
          <MuiTab label="External Compliance" />
          <MuiTab label="Submit & Finish" />
        </Tabs>
      </Box>

      {whitelistTabValue === 1 && renderWhitelistRulesSection()}
      {whitelistTabValue === 2 && <Review360Tab projectId={selectedProjectId || projectIdFromUrl || undefined} />}
      {whitelistTabValue === 7 && (
        <FinishSubmitTab
          projectId={selectedProjectId!}
          userId={userId}
          reviewStatus={(selectedProject as any)?.review_status ?? (selectedProject as any)?.reviewStatus ?? null}
          project={projectForReviewWorkflow}
          onReviewSubmitted={handleReviewSubmitted}
        />
      )}
      {whitelistTabValue === 0 && renderSensitiveAttributesSection()}
      {whitelistTabValue === 3 && <OwaspTab projectId={selectedProjectId!} userId={userId} />}
      {whitelistTabValue === 4 && <OntologyTab projectId={selectedProjectId!} userId={userId} />}
      {whitelistTabValue === 5 && <InternalComplianceTab projectId={selectedProjectId!} userId={userId} />}
      {whitelistTabValue === 6 && <ExternalComplianceTab projectId={selectedProjectId!} userId={userId} />}
    </div>
  );

  const renderTabContent = () => {
    // Show error only when we have no results data at all (from results API, project detail, or Redux)
    const reduxMatchesProject = !selectedProjectId || projectResultsFromRedux?.projectId === selectedProjectId;
    const hasResultsData =
      projectResultsData?.collection_results ||
      projectResultsData?.results ||
      projectDetail?.collection_results ||
      projectDetail?.results ||
      projectDetail?.feature_results ||
      (reduxMatchesProject && (projectResultsFromRedux?.collection_results || projectResultsFromRedux?.results));

    if (isProjectResultsLoading && !hasResultsData) {
      return (
        <div className="text-center py-5">
          <h5 className="text-muted">Loading project data...</h5>
        </div>
      );
    }

    if ((projectResultsData?.error || isError) && !hasResultsData) {
      return (
        <div className="text-center py-5">
          <h5 className="text-danger">Something went wrong.</h5>
          <p className="text-muted small mt-2">
            Could not load project results. If you have run analysis, ensure the results API returns data for this
            project.
          </p>
        </div>
      );
    }

    // Find the active control
    const activeControl = controls.find(c => c.control_title === activeTab);

    if (!activeControl) {
      return (
        <div className="text-center py-5">
          <p className="text-muted">No component found</p>
        </div>
      );
    }

    const normalizedControl = normalizeString(activeControl.control_title);

    // Show empty uploads state for Bias, Fairness, Ethics, Explainability when no uploads
    const isTabRequiringUploads =
      normalizedControl.includes('bias') ||
      normalizedControl.includes('fair') ||
      normalizedControl.includes('ethic') ||
      normalizedControl.includes('explain');
    if (!isProjectDetailLoading && isUploadsEmpty && isTabRequiringUploads) {
      return (
        <div className="text-center py-5">
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Upload Files
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 480, mx: 'auto' }}>
            Upload datasets, ML models, LLM models, and policy documents to get started with your project analysis.
          </Typography>
          <Button variant="phoenix-primary" onClick={handleUploadsNavClick} className="px-4">
            <FontAwesomeIcon icon={faCloudUpload} className="me-2" />
            Upload Files
          </Button>
        </div>
      );
    }

    const wrapFairnessWithBiasProbe = (fairnessEl: JSX.Element) => {
      if (!workbenchHasBiasTab) return fairnessEl;
      return (
        <>
          <div className="d-none" aria-hidden>
            <BiasDetection
              ref={biasDetectionRef}
              showHeaderVersionStrip={false}
              onVersionsReady={(ml, llm) => setBiasVersionCounts({ ml, llm })}
            />
          </div>
          {fairnessEl}
        </>
      );
    };

    if (normalizedControl.includes('bias')) {
      return (
        <BiasDetection
          ref={biasDetectionRef}
          showHeaderVersionStrip={false}
          onVersionsReady={(ml, llm) => setBiasVersionCounts({ ml, llm })}
        />
      );
    }

    if (normalizedControl.includes('fair')) {
      return wrapFairnessWithBiasProbe(
        <Fairness
          ref={fairnessRef}
          showHeaderVersionStrip={false}
          onVersionsReady={(ml, llm) => setFairnessVersionCounts({ ml, llm })}
        />
      );
    }

    if (normalizedControl.includes('explain')) {
      return (
        <Explainability
          ref={explainabilityRef}
          showInlineVersionControls={false}
          onVersionsReady={setExplainabilityNavInfo}
        />
      );
    }

    if (
      normalizedControl.includes('safety') ||
      normalizedControl.includes('abuse') ||
      (normalizedControl.includes('responsible') && normalizedControl.includes('ai'))
    ) {
      return (
        <AISafetyDetection ref={aiSafetyRef} showInlineVersionControls={false} onVersionsReady={setAiSafetyNavInfo} />
      );
    }

    if (normalizedControl.includes('prompt') && normalizedControl.includes('injection')) {
      return (
        <PromptInjection
          ref={promptInjectionRef}
          showInlineVersionControls={false}
          onVersionsReady={setPromptNavInfo}
        />
      );
    }

    // Attempt to match dynamic controls with results keys
    const results = projectResultsData?.results || {};
    const matchingKey = Object.keys(results).find(key => {
      const normalizedKey = normalizeString(key);
      return (
        normalizedKey === normalizedControl ||
        normalizedKey.includes(normalizedControl) ||
        normalizedControl.includes(normalizedKey)
      );
    });

    if (matchingKey) {
      if (normalizedControl.includes('safety') || normalizedControl.includes('abuse')) {
        return (
          <AISafetyDetection ref={aiSafetyRef} showInlineVersionControls={false} onVersionsReady={setAiSafetyNavInfo} />
        );
      }
      if (normalizedControl.includes('prompt') && normalizedControl.includes('injection')) {
        return (
          <PromptInjection
            ref={promptInjectionRef}
            showInlineVersionControls={false}
            onVersionsReady={setPromptNavInfo}
          />
        );
      }
    }

    // Audio (deepfake analysis) â€“ dimension control or tab titled "Audio"
    if (normalizedControl.includes('audio')) {
      const pid = selectedProjectId || '';
      const uid = userId || localStorage.getItem('userId') || '';
      return <AudioTab projectId={pid} userId={uid} audioUploads={audioUploadsList} />;
    }

    // AI Assurance 360Â° â€“ same graphs as standalone AI Assurance page
    if (normalizedControl.includes('ai') && normalizedControl.includes('assurance')) {
      if (selectedProjectId) return <AIAssuranceTab projectId={selectedProjectId} />;
    }

    // Agents â€” Ethico governance (Workbench dimension)
    if (normalizedControl === 'agents' || normalizedControl === 'agent') {
      if (selectedProjectId) {
        return <ChatConversationLifeSciences />;
      }
    }

    // Audit Report
    if (normalizedControl.includes('audit')) {
      if (selectedProjectId)
        return (
          <AuditReportTab
            projectId={selectedProjectId}
            userId={userId}
            initialAuditReport={generatedAuditReport}
            onReportGenerated={setGeneratedAuditReport}
          />
        );
    }

    return (
      <div className="text-center py-5">
        <div className="text-muted">No component found for {activeControl.control_title}</div>
      </div>
    );
  };

  const sidebarFixedPanelStyle = useMemo((): CSSProperties | undefined => {
    if (!sidebarFixedLayout) {
      return undefined;
    }
    return {
      position: 'fixed',
      left: sidebarFixedLayout.left,
      width: sidebarFixedLayout.width,
      top: 'var(--phoenix-navbar-top-height, 72px)',
      height: 'calc(100vh - var(--phoenix-navbar-top-height, 72px) - var(--phoenix-footer-height, 6rem))',
      zIndex: 2,
      overflowY: 'auto',
      overflowX: 'hidden',
      boxSizing: 'border-box'
    };
  }, [sidebarFixedLayout]);

  return (
    <div className="project-details-with-tabs">
      <style>{`
        .project-details-with-tabs {
          min-height: calc(100vh - var(--phoenix-navbar-top-height, 72px) - var(--phoenix-footer-height, 6rem));
          background: linear-gradient(293deg, rgb(237, 237, 237) 0%, rgb(255, 255, 255) 100%);
          padding: 0 24px;
          position: relative;
        }
        .project-details-with-tabs .nav-underline .nav-link {
          color: #6c757d;
          padding: 8px 0;
          margin-right: 24px;
          font-size: 15px;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
        }
        .project-details-with-tabs .nav-underline .nav-item.ms-auto {
          display: flex;
          align-items: center;
        }
        .project-details-with-tabs .nav-underline .nav-link:hover {
          color: #212529;
        }
        .project-details-with-tabs .nav-underline .nav-link.active {
          color: #212529;
          border-bottom-color: #20c997;
          font-weight: 600;
        }
        .project-details-with-tabs .sidebar-col {
          transition: width 0.3s ease;
        }
        .project-details-with-tabs .content-col {
          transition: width 0.3s ease;
        }
        .project-details-with-tabs .project-details-main-surface {
          border-radius: 0;
        }
        @media (min-width: 992px) {
          .project-details-with-tabs .content-col {
            align-self: stretch;
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          .project-details-with-tabs .project-details-main-surface {
            flex: 1 1 auto;
            min-height: calc(
              100vh - var(--phoenix-navbar-top-height, 72px) - var(--phoenix-footer-height, 6rem)
            );
          }
        }
        .project-details-with-tabs .project-details-sidebar-inner {
          background: transparent;
          border: none;
          border-radius: 0;
          padding: 0.875rem 0.625rem;
        }
        .project-details-with-tabs .project-details-sidebar-inner .list-group-item {
          border-radius: 8px;
        }
        .project-details-with-tabs .project-details-nav-item {
          padding: 10px 14px !important;
        }
        .project-details-with-tabs .project-details-nav-subitem {
          padding: 10px 14px 10px 20px !important;
        }
        .project-details-with-tabs .project-details-nav-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          width: 100%;
          min-width: 0;
        }
        .project-details-with-tabs .project-details-nav-row-main {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          flex: 1 1 auto;
        }
        .project-details-with-tabs .project-details-nav-chevron {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 1.5rem;
          height: 1.5rem;
          align-self: center;
        }
        .project-details-with-tabs .project-details-split {
          min-height: calc(
            100vh - var(--phoenix-navbar-top-height, 72px) - var(--phoenix-footer-height, 6rem)
          );
        }
        .project-details-with-tabs .project-details-sidebar-col {
          background: transparent;
        }
        /* Left column stretches with the row; fixed sidebar position is set via JS (sticky fails with body overflow-x). */
        @media (min-width: 992px) {
          .project-details-with-tabs .project-details-sidebar-col {
            align-self: stretch !important;
            min-height: calc(
              100vh - var(--phoenix-navbar-top-height, 72px) - var(--phoenix-footer-height, 6rem)
            );
          }
          .project-details-with-tabs .project-details-sidebar-panel-anchor {
            min-height: 0;
            height: 100%;
          }
        }
      `}</style>
      <style>{`
        .whitelist-carousel-wrapper .carousel-control-prev,
        .whitelist-carousel-wrapper .carousel-control-next {
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          opacity: 1;
        }
        .whitelist-carousel-wrapper .carousel-control-prev-icon,
        .whitelist-carousel-wrapper .carousel-control-next-icon {
          filter: invert(1);
        }
        .whitelist-carousel-wrapper .carousel-indicators {
          bottom: -48px;
        }
        .whitelist-carousel-wrapper .carousel-control-prev {
          left: -22px;
        }
        .whitelist-carousel-wrapper .carousel-control-next {
          right: -22px;
        }
      `}</style>
      {/* Project plan (left, under top bar) | Details + title (right) */}
      <Row className="g-0 justify-content-center align-items-lg-stretch flex-column flex-lg-row project-details-split">
        <Col
          xs={12}
          lg={3}
          xl={2}
          className="sidebar-col project-details-sidebar-col order-1 order-lg-0"
          style={{
            borderRight: '1px solid #e9ecef'
          }}
        >
          <div ref={sidebarPanelAnchorRef} className="project-details-sidebar-panel-anchor w-100">
            <div
              className="project-details-sidebar-scroll"
              style={{
                padding: '12px 12px 16px',
                ...sidebarFixedPanelStyle
              }}
            >
              <div className="project-details-sidebar-inner d-flex flex-column">
                <div className="fw-bold text-body mb-3" style={{ fontSize: '1rem', letterSpacing: '-0.01em' }}>
                  Project plan
                </div>
                <ListGroup variant="flush" className="border-0">
                  {/* Uploads */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'uploads' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={handleUploadsNavClick}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faCloudUpload}
                        size="sm"
                        className={activeNavItem === 'uploads' ? 'text-primary' : 'text-muted'}
                      />
                      <span
                        className={`fw-semibold ${activeNavItem === 'uploads' ? 'text-primary' : 'text-dark'}`}
                        style={{ fontSize: '14px' }}
                      >
                        Uploads
                      </span>
                    </div>
                  </ListGroup.Item>

                  {/* Summary */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'summary' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={handleSummaryClick}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        size="sm"
                        className={activeNavItem === 'summary' ? 'text-primary' : 'text-muted'}
                      />
                      <span
                        className={`fw-semibold ${activeNavItem === 'summary' ? 'text-primary' : 'text-dark'}`}
                        style={{ fontSize: '14px' }}
                      >
                        Project Summary
                      </span>
                    </div>
                  </ListGroup.Item>

                  {/* Workbench (dimension tabs) - Expandable */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'dimensions' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={() => toggleMenuItem('dimensions')}
                  >
                    <div className="project-details-nav-row">
                      <div className="project-details-nav-row-main">
                        <FontAwesomeIcon
                          icon={faLayerGroup}
                          size="sm"
                          className={activeNavItem === 'dimensions' ? 'text-primary' : 'text-muted'}
                        />
                        <span
                          className={`fw-semibold ${activeNavItem === 'dimensions' ? 'text-primary' : 'text-dark'}`}
                          style={{ fontSize: '14px' }}
                        >
                          Workbench
                        </span>
                      </div>
                      <span className="project-details-nav-chevron" aria-hidden>
                        <FontAwesomeIcon
                          icon={openMenuItems.includes('dimensions') ? faChevronDown : faChevronRight}
                          size="xs"
                          className="text-muted"
                        />
                      </span>
                    </div>
                  </ListGroup.Item>

                  {/* Dimension sub-items */}
                  {openMenuItems.includes('dimensions') &&
                    allDimensions.map(dimension => (
                      <ListGroup.Item
                        key={dimension.dimension_id}
                        className="border-0 px-0 mb-1 project-details-nav-subitem"
                        style={{
                          cursor: 'pointer',
                          backgroundColor:
                            selectedDimensionId === dimension.dimension_id ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                          borderRadius: '8px'
                        }}
                        onClick={() => handleNavItemClick('dimensions', dimension.dimension_id)}
                      >
                        <span
                          className={
                            selectedDimensionId === dimension.dimension_id ? 'fw-semibold text-body' : 'text-muted'
                          }
                          style={{ fontSize: '13px' }}
                        >
                          {dimension.dimension_title}
                        </span>
                      </ListGroup.Item>
                    ))}

                  {/* Whitelist */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'whitelist' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={handleWhitelistNavClick}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faListCheck}
                        size="sm"
                        className={activeNavItem === 'whitelist' ? 'text-primary' : 'text-muted'}
                      />
                      <span
                        className={`fw-semibold ${activeNavItem === 'whitelist' ? 'text-primary' : 'text-dark'}`}
                        style={{ fontSize: '14px' }}
                      >
                        Reviewer Studio
                      </span>
                    </div>
                  </ListGroup.Item>

                  {/* Audio */}
                  {/* <ListGroup.Item
                        className="border-0 px-0 mb-2"
                        style={{
                          cursor: 'pointer',
                          backgroundColor: activeNavItem === 'audio' ? '#f8f9fa' : 'transparent',
                          borderRadius: '4px',
                          padding: '8px 0'
                        }}
                        onClick={() => setActiveNavItem('audio')}
                      >
                        <div className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon
                            icon={faHeadphones}
                            size="sm"
                            className={activeNavItem === 'audio' ? 'text-primary' : 'text-muted'}
                          />
                          <span
                            className={`fw-semibold ${activeNavItem === 'audio' ? 'text-primary' : 'text-dark'}`}
                            style={{ fontSize: '14px' }}
                          >
                            Audio
                          </span>
                        </div>
                      </ListGroup.Item> */}

                  {/* AI Assurance */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'ai-assurance' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={handleAIAssuranceNavClick}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FontAwesomeIcon
                        icon={faShieldAlt}
                        size="sm"
                        className={activeNavItem === 'ai-assurance' ? 'text-primary' : 'text-muted'}
                      />
                      <span
                        className={`fw-semibold ${activeNavItem === 'ai-assurance' ? 'text-primary' : 'text-dark'}`}
                        style={{ fontSize: '14px' }}
                      >
                        AI Assurance
                      </span>
                    </div>
                  </ListGroup.Item>
                  {/* Agents â€” project-level dimension (after AI Assurance) */}
                  <ListGroup.Item
                    className="border-0 px-0 mb-2 project-details-nav-item"
                    style={{
                      cursor: 'pointer',
                      backgroundColor: activeNavItem === 'ethico' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                      borderRadius: '8px'
                    }}
                    onClick={() => toggleMenuItem('agents')}
                  >
                    <div className="project-details-nav-row">
                      <div className="project-details-nav-row-main">
                        <FontAwesomeIcon
                          icon={faRobot}
                          size="sm"
                          className={activeNavItem === 'ethico' ? 'text-primary' : 'text-muted'}
                        />
                        <span
                          className={`fw-semibold ${activeNavItem === 'ethico' ? 'text-primary' : 'text-dark'}`}
                          style={{ fontSize: '14px' }}
                        >
                          Agents
                        </span>
                      </div>
                      <span className="project-details-nav-chevron" aria-hidden>
                        <FontAwesomeIcon
                          icon={openMenuItems.includes('agents') ? faChevronDown : faChevronRight}
                          size="xs"
                          className="text-muted"
                        />
                      </span>
                    </div>
                  </ListGroup.Item>
                  {openMenuItems.includes('agents') && (
                    <ListGroup.Item
                      className="border-0 px-0 mb-1 project-details-nav-subitem"
                      style={{
                        cursor: 'pointer',
                        backgroundColor: activeNavItem === 'ethico' ? PROJECT_NAV_ACTIVE_BG : 'transparent',
                        borderRadius: '8px'
                      }}
                      onClick={handleEthicoNavClick}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon
                          icon={faClipboard}
                          size="sm"
                          className={activeNavItem === 'ethico' ? 'text-primary' : 'text-muted'}
                        />
                        <span
                          className={activeNavItem === 'ethico' ? 'fw-semibold text-body' : 'text-muted'}
                          style={{ fontSize: '13px' }}
                        >
                          Ethico
                        </span>
                      </div>
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={12} lg={9} xl={10} className="content-col min-w-0 order-2 order-lg-1">
          <div
            className="project-details-main-surface"
            style={{
              backgroundColor: '#ffffff',
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              overflow: 'visible',
              position: 'relative',
              zIndex: 1
            }}
          >
            {/* Project header â€” lives in the main column with detail content */}
            <div
              style={{
                padding: showHeaderLinearProgress ? '20px 24px 26px 24px' : '20px 24px 20px 24px',
                borderBottom: 'none',
                position: 'relative'
              }}
            >
              <div className="d-flex align-items-start justify-content-between mb-3">
                <div className="flex-grow-1">
                  <h2 className="mb-1 fw-bold" style={{ fontSize: '24px', color: '#212529', lineHeight: 1.25 }}>
                    {activeProjectTitle}
                  </h2>
                  <div className="d-flex align-items-center gap-3 text-muted mt-1" style={{ fontSize: '14px' }}>
                    <span>
                      <FontAwesomeIcon icon={faEdit} className="me-1" />
                      {activeProjectDomain}
                    </span>
                    <span>
                      <FontAwesomeIcon icon={faUser} className="me-1" />
                      {activeUsername}
                    </span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  {(normalizedUserRole === 'reviewer' || normalizedUserRole === 'admin') && (
                    <Button
                      variant="phoenix-primary"
                      size="sm"
                      className="px-3"
                      onClick={handleGenerateAuditReport}
                      disabled={isGeneratingAuditReport}
                    >
                      {isGeneratingAuditReport ? (
                        <>
                          <CircularProgress size={12} className="me-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                          Generate Audit Report
                        </>
                      )}
                    </Button>
                  )}
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="phoenix-secondary"
                      size="sm"
                      className="px-3 dropdown-caret-none"
                      id="project-actions-dropdown"
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="py-2" style={{ zIndex: 9999 }}>
                      <Dropdown.Item onClick={handleChangeProjectClick}>
                        <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                        Change Project
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleEditClick}>
                        <FontAwesomeIcon icon={faEdit} className="me-2" />
                        Edit
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleUploadClick}>
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Upload
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleRunAnalysis} disabled={isRunInProgress}>
                        <FontAwesomeIcon icon={faPlay} className="me-2" />
                        {isRunInProgress ? 'Running' : 'Run'}
                      </Dropdown.Item>
                      {canShowReviewerButton && (
                        <Dropdown.Item onClick={handleReviewClick}>
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          Reviewer Studio
                        </Dropdown.Item>
                      )}
                      {canShowSubmitForReviewButton && (
                        <Dropdown.Item onClick={handleSubmitForReview} disabled={isUpdatingProject}>
                          {isUpdatingProject ? (
                            <>
                              <CircularProgress size={12} className="me-2" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                              Submit for Review
                            </>
                          )}
                        </Dropdown.Item>
                      )}
                      {canShowDeveloperResetButton && (
                        <Dropdown.Item onClick={handleResetReviewStatus} disabled={isUpdatingProject}>
                          {isUpdatingProject ? (
                            <>
                              <CircularProgress size={12} className="me-2" />
                              Resetting...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faTimes} className="me-2" />
                              Reset
                            </>
                          )}
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              {showHeaderLinearProgress ? (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%'
                  }}
                >
                  {headerProgressLabel ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', px: 3, pt: 1.5, pb: 0.75, fontSize: '0.75rem', lineHeight: 1.2 }}
                    >
                      {headerProgressLabel}
                    </Typography>
                  ) : null}
                  <LinearProgress
                    sx={{
                      width: '100%',
                      height: 6,
                      borderRadius: 0,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#20c997'
                      }
                    }}
                  />
                </Box>
              ) : (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: 1,
                    backgroundColor: '#e9ecef'
                  }}
                />
              )}
            </div>

            {activeNavItem === 'dimensions' && (
              <div style={{ padding: '24px 32px' }}>
                {/* Tabs */}
                {controls.length > 0 ? (
                  <Tab.Container
                    id="project-details-tabs"
                    activeKey={activeTab || controls[0]?.control_title || ''}
                    onSelect={key => setActiveTab(key || '')}
                  >
                    <Nav
                      variant="underline"
                      className={`border-0 w-100 flex-wrap align-items-center gap-2 ${
                        normalizeString(activeTab || '').includes('bias') ||
                        normalizeString(activeTab || '').includes('fair')
                          ? 'mb-2'
                          : 'mb-4'
                      }`}
                    >
                      {controls.map(control => (
                        <Nav.Item key={control.control_title}>
                          <Nav.Link eventKey={control.control_title}>{control.control_title}</Nav.Link>
                        </Nav.Item>
                      ))}
                      {(() => {
                        const n = normalizeString(activeTab || '');
                        if (!activeTab) return null;

                        if (
                          workbenchHasBiasTab &&
                          n.includes('bias') &&
                          (biasVersionCounts.llm > 0 || biasVersionCounts.ml > 0)
                        ) {
                          return (
                            <Nav.Item className="ms-auto">
                              <BiasVersionHistoryStrip
                                llmCount={biasVersionCounts.llm}
                                mlCount={biasVersionCounts.ml}
                                onOpenLlm={() => biasDetectionRef.current?.handleHistoryOpen('llm')}
                                onOpenMl={() => biasDetectionRef.current?.handleHistoryOpen('ml')}
                              />
                            </Nav.Item>
                          );
                        }

                        if (
                          workbenchHasFairnessTab &&
                          n.includes('fair') &&
                          (fairnessVersionCounts.llm > 0 || fairnessVersionCounts.ml > 0)
                        ) {
                          return (
                            <Nav.Item className="ms-auto">
                              <FairnessVersionHistoryStrip
                                llmCount={fairnessVersionCounts.llm}
                                mlCount={fairnessVersionCounts.ml}
                                onOpenLlm={() => fairnessRef.current?.handleHistoryOpen('llm')}
                                onOpenMl={() => fairnessRef.current?.handleHistoryOpen('ml')}
                              />
                            </Nav.Item>
                          );
                        }

                        if (workbenchHasExplainTab && n.includes('explain') && explainabilityNavInfo.total > 0) {
                          const inf = explainabilityNavInfo;
                          return (
                            <Nav.Item className="ms-auto">
                              <BiasVersionHistoryStrip
                                llmCount={inf.runsWithLlm}
                                mlCount={inf.runsWithMl}
                                onOpenLlm={() => explainabilityRef.current?.handleHistoryOpen('llm')}
                                onOpenMl={() => explainabilityRef.current?.handleHistoryOpen('ml')}
                              />
                            </Nav.Item>
                          );
                        }

                        if (
                          workbenchHasAiSafetyTab &&
                          (n.includes('safety') ||
                            n.includes('abuse') ||
                            (n.includes('responsible') && n.includes('ai'))) &&
                          aiSafetyNavInfo.total > 0
                        ) {
                          const inf = aiSafetyNavInfo;
                          if (inf.runsWithMl > 0 || inf.runsWithLlm > 0) {
                            return (
                              <Nav.Item className="ms-auto">
                                <BiasVersionHistoryStrip
                                  llmCount={inf.runsWithLlm}
                                  mlCount={inf.runsWithMl}
                                  onOpenLlm={() => {
                                    aiSafetyRef.current?.setActiveLane('llm');
                                    aiSafetyRef.current?.handleHistoryOpen('llm');
                                  }}
                                  onOpenMl={() => {
                                    aiSafetyRef.current?.setActiveLane('ml');
                                    aiSafetyRef.current?.handleHistoryOpen('ml');
                                  }}
                                />
                              </Nav.Item>
                            );
                          }
                          return (
                            <Nav.Item className="ms-auto">
                              <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 lh-sm small text-body-secondary">
                                <span className="d-inline-flex align-items-center gap-2">
                                  <span>
                                    Runs <span className="text-body fw-medium">{inf.total}</span>{' '}
                                    {inf.total === 1 ? 'version' : 'versions'}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm text-decoration-none p-0 border-0"
                                    style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                                    onClick={() =>
                                      aiSafetyRef.current?.handleHistoryOpen(inf.runsWithLlm > 0 ? 'llm' : 'ml')
                                    }
                                  >
                                    History
                                  </button>
                                </span>
                              </div>
                            </Nav.Item>
                          );
                        }

                        if (
                          workbenchHasPromptTab &&
                          n.includes('prompt') &&
                          n.includes('injection') &&
                          promptNavInfo.total > 0
                        ) {
                          const inf = promptNavInfo;
                          if (inf.runsWithMl > 0 || inf.runsWithLlm > 0) {
                            return (
                              <Nav.Item className="ms-auto">
                                <BiasVersionHistoryStrip
                                  llmCount={inf.runsWithLlm}
                                  mlCount={inf.runsWithMl}
                                  onOpenLlm={() => promptInjectionRef.current?.handleHistoryOpen()}
                                  onOpenMl={() => promptInjectionRef.current?.handleHistoryOpen()}
                                />
                              </Nav.Item>
                            );
                          }
                          return (
                            <Nav.Item className="ms-auto">
                              <div className="d-flex flex-wrap align-items-center gap-2 gap-sm-3 lh-sm small text-body-secondary">
                                <span className="d-inline-flex align-items-center gap-2">
                                  <span>
                                    Prompt injection <span className="text-body fw-medium">{inf.total}</span>{' '}
                                    {inf.total === 1 ? 'version' : 'versions'}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm text-decoration-none p-0 border-0"
                                    style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                                    onClick={() => promptInjectionRef.current?.handleHistoryOpen()}
                                  >
                                    History
                                  </button>
                                </span>
                              </div>
                            </Nav.Item>
                          );
                        }

                        return null;
                      })()}
                    </Nav>

                    <Tab.Content>
                      <Tab.Pane eventKey={activeTab}>
                        <div style={{ minHeight: '300px' }}>{renderTabContent()}</div>
                      </Tab.Pane>
                    </Tab.Content>
                  </Tab.Container>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">No controls available</p>
                  </div>
                )}
              </div>
            )}

            {activeNavItem === 'uploads' && (
              <div style={{ padding: '24px 32px' }}>
                {isProjectDetailLoading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <CircularProgress size={24} />
                    <span className="ms-3 text-muted">Loading uploads...</span>
                  </div>
                ) : isUploadsEmpty ? (
                  <div className="text-center py-5">
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      Upload Files
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 480, mx: 'auto' }}>
                      Upload datasets, ML models, LLM models, and policy documents to get started with your project
                      analysis.
                    </Typography>
                    <Button variant="phoenix-primary" onClick={handleUploadsNavClick} className="px-4">
                      <FontAwesomeIcon icon={faCloudUpload} className="me-2" />
                      Upload Files
                    </Button>
                  </div>
                ) : (
                  <div>
                    <h4 className="mb-4">Uploads</h4>
                    <Row className="g-4">
                      <Col md={3}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">Datasets</h6>
                          <p className="fs-4 fw-bold mb-0">{datasetUploads.length}</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">ML Models</h6>
                          <p className="fs-4 fw-bold mb-0">{mlModelUploads.length}</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">LLM Models</h6>
                          <p className="fs-4 fw-bold mb-0">{llmModelUploads.length}</p>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">Policy Documents</h6>
                          <p className="fs-4 fw-bold mb-0">{policyDocuments.length}</p>
                        </div>
                      </Col>
                    </Row>
                    <div className="mt-4">
                      <Button variant="phoenix-primary" onClick={handleUploadsNavClick} className="px-4">
                        <FontAwesomeIcon icon={faCloudUpload} className="me-2" />
                        Add More Files
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeNavItem === 'whitelist' && renderWhitelistContent()}

            {/* {activeNavItem === 'audio' && (
                  <AudioTab
                    projectId={selectedProjectId || ''}
                    userId={userId || localStorage.getItem('userId') || ''}
                    audioUploads={audioUploadsList}
                  />
                )} */}

            {activeNavItem === 'audit-report' && (
              <AuditReportTab
                projectId={selectedProjectId!}
                userId={userId}
                initialAuditReport={generatedAuditReport}
                onReportGenerated={setGeneratedAuditReport}
              />
            )}
            {activeNavItem === 'ai-assurance' && selectedProjectId && <AIAssuranceTab projectId={selectedProjectId} />}
            {activeNavItem === 'ethico' && selectedProjectId && <ChatConversationLifeSciences />}
            {activeNavItem === 'summary' && (
              <div style={{ padding: '24px 32px' }}>
                <h4 className="mb-4">Project Summary</h4>
                {isProjectDetailLoading ? (
                  <div className="d-flex align-items-center justify-content-center py-5">
                    <CircularProgress size={24} />
                    <span className="ms-3 text-muted">Loading project details...</span>
                  </div>
                ) : projectDetailError ? (
                  <div className="p-3 border border-danger rounded-3 bg-danger-subtle text-danger">
                    {projectDetailError}
                  </div>
                ) : projectDetail ? (
                  <>
                    <div className="mb-4">
                      <h5 className="fw-semibold mb-2">{projectDetail.title || selectedProject?.title || 'Project'}</h5>
                      <p className="text-muted mb-0">
                        {projectDetail.overview || 'No overview has been provided for this project.'}
                      </p>
                    </div>

                    <Row className="g-4 mb-4">
                      <Col md={4}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">Project Info</h6>
                          <ul className="list-unstyled small mb-0">
                            <li>
                              <strong>Project ID:</strong> {projectDetail.projectId}
                            </li>
                            <li>
                              <strong>Domain:</strong> {projectDetail.domain || selectedProject?.domain || 'N/A'}
                            </li>
                            <li>
                              <strong>Environment:</strong>{' '}
                              {projectDetail.environment || localStorage.getItem('SELECTED_ENV') || 'N/A'}
                            </li>
                            <li>
                              <strong>User ID:</strong> {projectDetail.userId || 'N/A'}
                            </li>
                          </ul>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">Uploads</h6>
                          <ul className="list-unstyled small mb-0">
                            <li>
                              <strong>Datasets:</strong> {datasetUploads.length}
                            </li>
                            <li>
                              <strong>ML Models:</strong> {mlModelUploads.length}
                            </li>
                            <li>
                              <strong>LLM Models:</strong> {llmModelUploads.length}
                            </li>
                            <li>
                              <strong>Policy Documents:</strong> {policyDocuments.length}
                            </li>
                            {llmBiasAggregate?.dataset_verdict && (
                              <li className="mt-2">
                                <strong>Latest LLM Verdict:</strong> {llmBiasAggregate.dataset_verdict}
                              </li>
                            )}
                          </ul>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div className="p-3 border rounded-3 h-100 bg-light-subtle">
                          <h6 className="text-uppercase text-muted fw-semibold mb-2">Timeline</h6>
                          <ul className="list-unstyled small mb-0">
                            <li>
                              <strong>Created:</strong> {formatDate(projectDetail.created_at)}
                            </li>
                            <li>
                              <strong>Updated:</strong> {formatDate(projectDetail.updated_at)}
                            </li>
                            <li>
                              <strong>Last Activity:</strong> {formatDate(projectDetail.lastUpdated)}
                            </li>
                          </ul>
                        </div>
                      </Col>
                    </Row>

                    {datasetUploads.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">Datasets</h6>
                        <Row className="g-3">
                          {datasetUploads.slice(0, 4).map((datasetItem: any, index: number) => {
                            const missingEntries = Object.entries(datasetItem?.missing_values || {}).slice(0, 5);
                            return (
                              <Col xs={12} md={6} key={`dataset-${index}`}>
                                <div className="p-3 border rounded-3 h-100">
                                  <h6 className="fw-semibold mb-1">Dataset {index + 1}</h6>
                                  <p className="text-muted small mb-2">{extractFileName(datasetItem?.file_name)}</p>
                                  <div className="small mb-1">
                                    <strong>Rows:</strong> {datasetItem?.num_rows ?? 'N/A'}
                                  </div>
                                  <div className="small mb-1">
                                    <strong>Columns:</strong> {datasetItem?.num_columns ?? 'N/A'}
                                  </div>
                                  <div className="small mb-1">
                                    <strong>Uploaded:</strong> {formatDate(datasetItem?.upload_date)}
                                  </div>
                                  <div className="small mb-1">
                                    <strong>Preprocessed:</strong> {datasetItem?.preprocessed ? 'Yes' : 'No'}
                                  </div>
                                  {missingEntries.length > 0 && (
                                    <div className="mt-2">
                                      <div className="small text-muted fw-semibold mb-1">Top Missing Values</div>
                                      <ul className="list-unstyled small mb-0">
                                        {missingEntries.map(([key, value]) => (
                                          <li key={`${datasetItem?.file_name}-${key}`}>
                                            {key}: {value as any}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </Col>
                            );
                          })}
                        </Row>
                      </div>
                    )}

                    {mlModelUploads.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">ML Models</h6>
                        <Row className="g-3">
                          {mlModelUploads.map((modelItem: any, index: number) => (
                            <Col xs={12} key={`ml-model-${index}`}>
                              <div className="p-3 border rounded-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                  <div>
                                    <h6 className="fw-semibold mb-1">Model {index + 1}</h6>
                                    <p className="text-muted small mb-0">{extractFileName(modelItem?.file_name)}</p>
                                  </div>
                                  <div className="d-flex flex-wrap gap-3 small text-muted">
                                    <span>
                                      <strong>Type:</strong>{' '}
                                      {modelItem?.model_metadata?.model_type ||
                                        modelItem?.model_metadata?.model ||
                                        'N/A'}
                                    </span>
                                    <span>
                                      <strong>Uploaded:</strong> {formatDate(modelItem?.upload_date)}
                                    </span>
                                    <span>
                                      <strong>Sensitive Attributes:</strong>{' '}
                                      {(modelItem?.sensitive_attributes || []).length}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    {llmModelUploads.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">LLM Models</h6>
                        <Row className="g-3">
                          {llmModelUploads.map((modelItem: any, index: number) => (
                            <Col xs={12} key={`llm-model-${index}`}>
                              <div className="p-3 border rounded-3">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                                  <div>
                                    <h6 className="fw-semibold mb-1">LLM Model {index + 1}</h6>
                                    <p className="text-muted small mb-0">
                                      {modelItem?.title || extractFileName(modelItem?.file_name) || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="d-flex flex-wrap gap-3 small text-muted">
                                    <span>
                                      <strong>Domain:</strong> {modelItem?.domain || 'N/A'}
                                    </span>
                                    <span>
                                      <strong>Entries:</strong>{' '}
                                      {Array.isArray(modelItem?.data) ? modelItem.data.length : 0}
                                    </span>
                                    <span>
                                      <strong>Uploaded:</strong> {formatDate(modelItem?.upload_date)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    {policyDocuments.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">Policy Documents</h6>
                        <Row className="g-3">
                          {policyDocuments.slice(0, 4).map((policyItem: any, index: number) => (
                            <Col xs={12} md={6} key={`policy-${index}`}>
                              <div className="p-3 border rounded-3 h-100">
                                <h6 className="fw-semibold mb-1">Policy Document {index + 1}</h6>
                                <p className="text-muted small mb-2">{extractFileName(policyItem?.file_name)}</p>
                                <div className="small mb-1">
                                  <strong>Uploaded:</strong> {formatDate(policyItem?.upload_date)}
                                </div>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}

                    {whitelistEntries.length > 0 && (
                      <div className="mb-4">
                        <h6 className="fw-semibold mb-3">Whitelist Rules</h6>
                        <div className="whitelist-carousel-wrapper position-relative">
                          <Carousel
                            interval={null}
                            indicators={whitelistEntries.length > 4}
                            controls={whitelistEntries.length > 4}
                            variant="dark"
                          >
                            {Array.from({
                              length: Math.ceil(whitelistEntries.length / 4)
                            }).map((_, slideIndex) => {
                              const start = slideIndex * 4;
                              const slideItems = whitelistEntries.slice(start, start + 4);

                              return (
                                <Carousel.Item key={`whitelist-slide-${slideIndex}`}>
                                  <Row className="g-3">
                                    {slideItems.map(([ruleKey, ruleValue]) => (
                                      <Col xs={12} sm={6} lg={3} key={`rule-${ruleKey}`}>
                                        <div className="p-3 border rounded-3 bg-light-subtle h-100">
                                          <h6
                                            className="fw-semibold mb-2"
                                            style={{
                                              textTransform: 'capitalize'
                                            }}
                                          >
                                            {ruleKey.replace(/_/g, ' ')}
                                          </h6>
                                          <div className="small text-muted mb-2">Regulatory Bodies</div>
                                          <div className="d-flex flex-wrap gap-2 mb-3">
                                            {(ruleValue?.regulatory_body || []).length > 0 ? (
                                              (ruleValue?.regulatory_body || []).map((item: string, idx: number) => (
                                                <span
                                                  key={`${ruleKey}-reg-${idx}`}
                                                  className="badge bg-primary-subtle text-primary px-2 py-1"
                                                >
                                                  {item}
                                                </span>
                                              ))
                                            ) : (
                                              <span className="small text-muted">No regulatory bodies specified.</span>
                                            )}
                                          </div>
                                          <div className="small text-muted">
                                            {ruleValue?.justification ? (
                                              <em>{ruleValue.justification}</em>
                                            ) : (
                                              'No justification provided.'
                                            )}
                                          </div>
                                        </div>
                                      </Col>
                                    ))}
                                  </Row>
                                </Carousel.Item>
                              );
                            })}
                          </Carousel>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted">No project details available.</p>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Upload Modal */}
      <Modal
        open={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        aria-labelledby="upload-modal-title"
        aria-describedby="upload-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '90vw', md: 1000 },
            maxWidth: 1000,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'auto',
            outline: 'none'
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid #e9ecef'
            }}
          >
            <Typography id="upload-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Upload Files
            </Typography>
            <IconButton onClick={handleCloseUploadModal} size="small" sx={{ color: 'text.secondary' }}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <InventoryTab uploadMode="tsc" />
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              p: 3,
              borderTop: '1px solid #e9ecef'
            }}
          >
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Dropdown>
                <Dropdown.Toggle
                  variant="phoenix-secondary"
                  className="px-3 dropdown-caret-none"
                  id="modal-actions-dropdown"
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </Dropdown.Toggle>
                <Dropdown.Menu className="py-2" style={{ zIndex: 9999 }}>
                  {canShowReviewerButton && (
                    <Dropdown.Item onClick={handleReviewClick}>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                      Review
                    </Dropdown.Item>
                  )}
                  {canShowSubmitForReviewButton && (
                    <Dropdown.Item onClick={handleSubmitForReview} disabled={isUpdatingProject}>
                      {isUpdatingProject ? (
                        <>
                          <CircularProgress size={12} className="me-2" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                          Submit for Review
                        </>
                      )}
                    </Dropdown.Item>
                  )}
                  {canShowDeveloperResetButton && (
                    <Dropdown.Item onClick={handleResetReviewStatus} disabled={isUpdatingProject}>
                      {isUpdatingProject ? (
                        <>
                          <CircularProgress size={12} className="me-2" />
                          Resetting...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faTimes} className="me-2" />
                          Reset
                        </>
                      )}
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              <Button variant="phoenix-primary" className="px-3" onClick={handleCompleteClick}>
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Complete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Review Modal */}
      <Modal open={isReviewModalOpen} onClose={handleCloseReviewModal} aria-labelledby="review-modal-title">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 600 },
            maxWidth: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            p: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography id="review-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Review Uploads
            </Typography>
            <IconButton onClick={handleCloseReviewModal} size="small" sx={{ color: 'text.secondary' }}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Review your uploaded files here. This feature can be expanded to show detailed information about uploaded
            files.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="secondary" onClick={handleCloseReviewModal}>
              Close
            </Button>
            <Button variant="phoenix-primary" onClick={handleCompleteClick}>
              Proceed
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Change Project Modal */}
      <Modal
        open={isChangeProjectModalOpen}
        onClose={handleCloseChangeProjectModal}
        aria-labelledby="change-project-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 700 },
            maxWidth: 700,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'auto'
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid #e9ecef',
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
              zIndex: 1
            }}
          >
            <Typography id="change-project-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Change Project
            </Typography>
            <IconButton onClick={handleCloseChangeProjectModal} size="small" sx={{ color: 'text.secondary' }}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Select a project to view
            </Typography>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {projectList.map((project: Project) => (
                <div
                  key={project.projectId}
                  onClick={() => handleProjectSelect(project.projectId)}
                  style={{
                    padding: '16px',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    backgroundColor: selectedProjectId === project.projectId ? '#f8f9fa' : '#fff'
                  }}
                  onMouseEnter={e => {
                    if (selectedProjectId !== project.projectId) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={e => {
                    if (selectedProjectId !== project.projectId) {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {project.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {project.domain} â€¢ {project.overview?.substring(0, 50) || 'No description'}
                  </Typography>
                </div>
              ))}
            </div>
          </Box>
        </Box>
      </Modal>

      {/* Edit Project Modal */}
      <Modal
        open={isEditProjectModalOpen}
        onClose={handleCloseEditProjectModal}
        aria-labelledby="edit-project-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 600 },
            maxWidth: 600,
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            overflow: 'auto'
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 3,
              borderBottom: '1px solid #e9ecef'
            }}
          >
            <Typography id="edit-project-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Edit Project
            </Typography>
            <IconButton onClick={handleCloseEditProjectModal} size="small" sx={{ color: 'text.secondary' }}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>

          {/* Modal Content */}
          <Box sx={{ p: 3 }}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Project Title</Form.Label>
                <Form.Control
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  placeholder="Enter project title"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Domain</Form.Label>
                <Form.Select value={editDomain} onChange={e => setEditDomain(e.target.value)}>
                  <option value="">Select Domain</option>
                  <option value="Banking">Banking</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="LifeSciences">LifeSciences</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Pharma">Pharma</option>
                  <option value="CPG">CPG</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Overview</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editOverview}
                  onChange={e => setEditOverview(e.target.value)}
                  placeholder="Enter project overview"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Environment</Form.Label>
                <Form.Select value={editEnvironment} onChange={e => setEditEnvironment(e.target.value)}>
                  <option value="DEV">DEV</option>
                  <option value="PROD">PROD</option>
                </Form.Select>
              </Form.Group>
            </Form>
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              p: 3,
              borderTop: '1px solid #e9ecef'
            }}
          >
            <Button variant="secondary" onClick={handleCloseEditProjectModal}>
              Cancel
            </Button>
            <Button variant="phoenix-primary" onClick={handleSaveProject} disabled={isUpdatingProject}>
              {isUpdatingProject ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Loading Backdrop — project analysis */}
      <Backdrop sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={isCompleteTriggered}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography component="p" sx={{ color: 'inherit', m: 0, ml: 2 }}>
            {activeApi}
          </Typography>
        </Box>
      </Backdrop>

      {/* Full-screen overlay — Reviewer Studio governance + project sync */}
      <Backdrop sx={theme => ({ color: '#fff', zIndex: theme.zIndex.drawer + 2 })} open={isReviewerGovernanceLoading}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            px: 3,
            textAlign: 'center'
          }}
        >
          <CircularProgress color="inherit" size={48} />
          <Typography component="p" sx={{ color: 'inherit', m: 0, maxWidth: 360 }}>
            {reviewerGovernanceLoaderLabel}
          </Typography>
        </Box>
      </Backdrop>
    </div>
  );
};

export default ProjectDetailsWithTabs;
